import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import {
  ArrowLeft,
  Camera,
  ChevronDown,
  ClipboardList,
  Download,
  Eye,
  FileText,
  Gavel,
  Info,
  Plus,
  Radar,
  RefreshCw,
  Rocket,
  Snowflake,
  Timer,
  TriangleAlert,
  Trophy,
  Users,
  X,
} from 'lucide-react'
import { Fragment, useState } from 'react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import {
  Count,
  EmptyState,
  Page,
  PageHeader,
  Pager,
  StatCard,
  TableCard,
  Toolbar,
} from '@/components/shell'
import {
  api,
  ApiError,
  type SupernovaCampaign,
  type SupernovaCampaignRow,
  type SupernovaLeaderboardPage,
  type SupernovaLeaderboardRow,
  type SupernovaOverview,
  type SupernovaPartnerSync,
  type SupernovaRegistrationRow,
  type SupernovaRegistrations,
  type SupernovaSettings,
  type SupernovaTokenOverrideRow,
  type SupernovaTokenCandidates,
  type SupernovaUnlock,
} from '@/lib/api'
import { API_BASE } from '@/lib/config'
import {
  explorerAddressUrl,
  explorerTokenUrl,
  formatDateTime,
  formatUnits,
  isUnknownQuote,
  quoteLabel,
  shortAddress,
  LAUNCH_TOKEN_DECIMALS,
} from '@/lib/format'
import { useCan } from '@/lib/session'
import { cn } from '@/lib/utils'

import {
  addBatchSummary,
  BATCH_BUCKETS,
  BATCH_BUCKET_LABELS,
  BOARDS,
  BOARD_LABELS,
  BOARD_METRIC_LABELS,
  EMPTY_PRIZE_POOL,
  ENTRY_SWITCH_LABEL,
  FIELD_HINTS,
  IMPORT_BUCKETS,
  IMPORT_BUCKET_LABELS,
  IMPORT_LIST_LIMIT,
  LADDER_MAX_ROWS,
  OVERRIDE_MODE_META,
  PER_PAGE,
  PRIZE_MODES,
  PRIZE_MODE_LABELS,
  REGISTRATION_SOURCES,
  REGISTRATION_SOURCE_META,
  TIER_BASIS_LABELS,
  TIER_BASIS_MODES,
  TIER_SPLITS,
  TIER_SPLIT_LABELS,
  V16_CURRENCY,
  appliedSummary,
  PHASE_META,
  QueryError,
  RESULT_STATUS_META,
  RawId,
  ReasonField,
  STATUS_META,
  batchSummary,
  buildContent,
  buildPrizePool,
  entryMeta,
  errorMessage,
  evenSplit,
  Field,
  fieldLabel,
  formatBlock,
  formatUsd,
  fromLocalInputValue,
  importSummary,
  isDenylistRow,
  lockedFieldsFromError,
  maxMcapSamples,
  mcapSampleHint,
  mergeOverrides,
  parseAddressPaste,
  readBatchResult,
  readAddBatchResult,
  readContent,
  readImportResult,
  readPrizePool,
  readRules,
  reasonValid,
  rowReasonLabel,
  RULE_KEYS,
  ruleValid,
  sampleHealth,
  TIER_MAX_RANK,
  TIER_MAX_ROWS,
  TIER_MODES,
  TIER_MODE_LABELS,
  defaultTiers,
  formatPct,
  isRowIssue,
  ladderIssueMessage,
  poolUsable,
  restTier,
  splitTotal,
  splitValid,
  summarizeTiers,
  tierCalc,
  tierIssueMessage,
  tierKind,
  tierSummaryText,
  toLocalInputValue,
  toPctTiers,
  useConfirm,
  v16PrizePool,
  validateLadder,
  validateTiers,
  type Board,
  type ContentInput,
  type DisqualifyBatchResult,
  type AddBatchResult,
  type ImportRegistrationsResult,
  type LadderInput,
  type LadderIssue,
  type PendingAction,
  type PrizeMode,
  type PrizePoolInput,
  type RuleKey,
  type RulesInput,
  type TierBasisMode,
  type TierInput,
  type TierIssue,
  type TierMode,
  type TierSplit,
} from './-shared'

/**
 * 页面的五个分区。
 *
 * 这一页竖着堆五个区块时有三四屏高，而运营的每一次进来只办其中一件事 ——
 * 改配置、核报名、看榜。**次序就是这个页面被使用的次序**：先看状态，再改配置，
 * 然后才是名单与榜单。
 */
const TABS = [
  { value: 'overview', label: '概览' },
  { value: 'config', label: '活动配置' },
  { value: 'registrations', label: '报名名单' },
  { value: 'tokens', label: '代币资格' },
  { value: 'leaderboard', label: '榜单预览' },
] as const

type TabKey = (typeof TABS)[number]['value']

const TAB_KEYS: readonly string[] = TABS.map((tab) => tab.value)

/** 认不出的 tab 一律回概览。URL 是可以被手改、被转发、被收藏的，不能假设它合法。 */
function parseTab(value: unknown): TabKey {
  return TAB_KEYS.includes(value as string) ? (value as TabKey) : 'overview'
}

/**
 * 当前 tab 记在 URL 的 `?tab=` 上，而不是组件里的 `useState`。
 *
 * 运营之间是靠**贴链接**交接的（「这一期的报名名单你看一下」）。存在组件状态里的话，
 * 收到链接的人落在概览页，还得自己再点一次 —— 而刷新一下同样会把人弹回概览，
 * 那正是改完配置按 F5 之后最不想发生的事。
 *
 * 认不出的值在这里**归成 `undefined`，不在这里替换成 `overview`**（与 wallets /
 * trades / traders 三个兄弟路由同一种写法）。回落是组件的事，交给 `parseTab`：
 * 在这一层写死默认值的话，一个什么都没带的链接会被路由改写成 `?tab=overview`，
 * 于是每个复制出去的链接都钉死了一个本来不必出现的参数。
 *
 * 声明成必填也不行 —— TanStack 会要求每一个指向这一页的 `<Link>` 都带上 `search`。
 */
export const Route = createFileRoute('/dashboard/supernova/$id')({
  validateSearch: (search: Record<string, unknown>): { tab?: TabKey } => ({
    tab: TAB_KEYS.includes(search.tab as string) ? (search.tab as TabKey) : undefined,
  }),
  component: SupernovaCampaignPage,
})

/**
 * 一期 SuperNova 活动的详情页。
 *
 * 页面里的**每一个写操作都要理由、都写审计、都走二次确认**，所以它们全部经由
 * `-shared.tsx` 的 `useConfirm` 这一个入口（列表页的全局设置也用同一个）。
 *
 * ⚠️ 前台入口开关**不在这一页**：它从 phase 5 起是全局的，挂在活动列表页顶部的
 * 「全局设置」里。这里只把它的当前状态摆出来并指路。
 */

// ── 页面 ────────────────────────────────────────────────────────────────

function SupernovaCampaignPage() {
  const { id } = Route.useParams()
  // 再过一次 `parseTab`：search 的类型允许 tab 缺席，而 Tabs 的 value 不能是 undefined。
  const tab = parseTab(Route.useSearch().tab)
  const navigate = Route.useNavigate()
  const confirm = useConfirm()

  const overview = useQuery({
    queryKey: ['supernova-overview', id],
    queryFn: () => api.get<SupernovaOverview>(`/supernova/campaigns/${id}/overview`),
    retry: false,
    refetchInterval: 60_000,
  })

  /**
   * 全局设置。这一页要它办两件事：把「入口总开关现在开着还是关着」摆给人看，
   * 以及把采样间隔代进「市值样本数 ≈ 窗口 ÷ 采样间隔」那句提示。
   *
   * **读不到不影响这一页**：`settings` 为 undefined 时提示退化成不带数字的那一版，
   * 表单照常可用。为一句提示把整页打成错误页不成比例。
   */
  const settings = useQuery({
    queryKey: ['supernova-settings'],
    queryFn: () => api.get<{ data: SupernovaSettings }>('/supernova/settings'),
    retry: false,
    refetchInterval: 60_000,
  })

  const sampleIntervalMin = settings.data?.data.sampleIntervalMin ?? null

  return (
    <Page>
      <PageHeader
        title={overview.data?.campaign.name ?? id}
        description={`活动 ID ${id}。这一页的每个写操作都要填理由，并写入审计流水。`}
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm" className="pressable rounded-apple-sm">
              <Link to="/dashboard/supernova">
                <ArrowLeft className="size-3.5" />
                全部活动
              </Link>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="pressable rounded-apple-sm"
              disabled={overview.isFetching}
              onClick={() => void overview.refetch()}
            >
              <RefreshCw className={cn('size-3.5', overview.isFetching && 'animate-spin')} />
              刷新
            </Button>
          </div>
        }
      />

      {overview.isPending && <Skeleton className="h-64 w-full rounded-apple" />}

      {overview.isError && (
        <QueryError
          title="读不到这一期活动"
          error={overview.error}
          onRetry={() => void overview.refetch()}
        />
      )}

      {/*
        五个分区只挂**未激活就不渲染**的 Radix Tabs（默认行为，别加 forceMount）。
        报名名单、代币覆盖、榜单各自有一条查询，同屏渲染等于每次进这一页都打四条请求，
        而运营这一次多半只看其中一条。切走再切回会重新挂载、面板状态归零 ——
        这是可以接受的：真正的结果都已经落在下面的列表里了。
      */}
      {overview.isSuccess && (
        <Tabs
          value={tab}
          className="gap-4"
          onValueChange={(value) =>
            void navigate({ search: { tab: parseTab(value) }, replace: true })
          }
        >
          <TabsList>
            {TABS.map((item) => (
              <TabsTrigger key={item.value} value={item.value} className="h-9 text-[13px]">
                {item.label}
                {/*
                  报名人数是这一页最常被问的一个数字，摆在标签上就不用为了看一眼切过去。
                  它取的是概览那条查询已经有的计数，不新增请求。
                */}
                {item.value === 'registrations' && (
                  <span className="tnum font-mono text-[11px] text-mut2">
                    {overview.data.registrations.registered}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <StatusSection data={overview.data} />
            <UnlockPanel
              resolved={overview.data.resolvedPrizePool}
              peakUsd={overview.data.campaign.ladderPeakVolumeUsd}
            />
            <ExposureSection
              id={id}
              campaign={overview.data.campaign}
              entryVisible={settings.data?.data.entryVisible ?? null}
              ask={confirm.ask}
            />
            <ActionsSection id={id} data={overview.data} ask={confirm.ask} />
          </TabsContent>

          <TabsContent value="config">
            <CampaignForm
              key={overview.data.campaign.updatedAt}
              id={id}
              campaign={overview.data.campaign}
              lockedFields={overview.data.lockedFields}
              sampleIntervalMin={sampleIntervalMin}
              resolvedPrizePool={overview.data.resolvedPrizePool}
            />
          </TabsContent>

          <TabsContent value="registrations">
            <RegistrationsSection
              id={id}
              campaignName={overview.data.campaign.name}
              bySource={overview.data.registrations.bySource}
              ask={confirm.ask}
            />
          </TabsContent>

          <TabsContent value="tokens">
            <TokenOverridesSection
              id={id}
              ask={confirm.ask}
              frozen={overview.data.phase === 'frozen'}
            />
          </TabsContent>

          <TabsContent value="leaderboard">
            <LeaderboardSection id={id} />
          </TabsContent>
        </Tabs>
      )}

      {confirm.dialog}
    </Page>
  )
}

// ── 状态区 ──────────────────────────────────────────────────────────────

function StatusSection({ data }: { data: SupernovaOverview }) {
  const {
    campaign,
    phase,
    latestSnapshot,
    indexerCursor,
    registrations,
    openingBalances,
    sampler,
  } = data
  const phaseMeta = PHASE_META[phase] ?? PHASE_META.unconfigured!
  const resultMeta = RESULT_STATUS_META[campaign.resultStatus]
  const now = Date.now()

  return (
    <>
      <section className="surface space-y-5 p-6">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex items-center gap-2.5">
            <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
              <Rocket className="size-4 text-cyan" />
            </div>
            <div>
              <h2 className="text-[15px] font-medium tracking-tight text-txt">活动状态</h2>
              <p className="mt-0.5 text-[12px] text-mut2">
                时间由后台配置，区块号由快照任务解析后写回；冻结后终榜不再变化。
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={cn('h-6 text-[11px]', phaseMeta.className)}>{phaseMeta.label}</Badge>
            {/*
              「当前期」而不是入口开关：入口是全局的（列表页顶部的全局设置），
              这里只说这一期在不在前台展示的位置上。只读角色也要看得见这一点 ——
              下面的「前台曝光」区块只对有写权限的人渲染。
            */}
            {campaign.isCurrent && (
              <Badge className="h-6 bg-cyan/15 text-[11px] text-cyan hover:bg-cyan/15">
                当前期
              </Badge>
            )}
            <Badge
              className={cn(
                'h-6 text-[11px]',
                resultMeta?.className ?? 'bg-edge/10 text-mut hover:bg-edge/10',
              )}
            >
              {resultMeta?.label ?? campaign.resultStatus}
            </Badge>
            {campaign.frozenAt && (
              <Badge className="h-6 bg-cyan/15 text-[11px] text-cyan hover:bg-cyan/15">
                已冻结 · {formatDateTime(campaign.frozenAt)}
              </Badge>
            )}
          </div>
        </div>

        {phase === 'unconfigured' && (
          <div className="flex items-start gap-2 rounded-apple bg-warn/[0.07] px-3.5 py-2.5 text-[11.5px] leading-relaxed text-warn">
            <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
            <span className="min-w-0 flex-1">
              这一期还没有起止时间。前台会显示「活动即将公布」，报名与快照接口在此之前一律拒绝。
            </span>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="开始时间">{formatDateTime(campaign.startAt)}</Field>
          <Field label="结束时间">{formatDateTime(campaign.endAt)}</Field>
          <Field label="起始区块">
            <span className="tnum font-mono text-[11.5px]">{formatBlock(campaign.startBlock)}</span>
          </Field>
          <Field label="结束区块">
            <span className="tnum font-mono text-[11.5px]">{formatBlock(campaign.endBlock)}</span>
          </Field>
          <Field label="规则版本">
            <span className="font-mono text-[11.5px]">{campaign.rulesVersion}</span>
          </Field>
          <Field label="快照间隔">{`${campaign.snapshotIntervalMin} 分钟`}</Field>
          <Field label="索引游标">
            <span className="tnum font-mono text-[11.5px]">
              {formatBlock(indexerCursor?.lastBlock ?? null)}
            </span>
          </Field>
          <Field label="最近修改">
            {`${formatDateTime(campaign.updatedAt)} · ${campaign.updatedBy ?? '未知账号'}`}
          </Field>
        </div>
      </section>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          icon={Users}
          label="已报名"
          value={registrations.registered}
          tone="green"
          hint="计入榜单的参赛钱包"
        />
        <StatCard
          icon={TriangleAlert}
          label="已取消资格"
          value={registrations.disqualified}
          tone={registrations.disqualified > 0 ? 'rose' : 'default'}
          hint="不计入任何榜单"
        />
        <StatCard
          icon={ClipboardList}
          label="初始持仓行"
          value={openingBalances}
          hint="报名时刻的持仓，盈亏榜的初始批次"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <SnapshotCard snapshot={latestSnapshot} />
        <SamplerCard sampler={sampler} now={now} />
      </div>
    </>
  )
}

function SnapshotCard({ snapshot }: { snapshot: SupernovaOverview['latestSnapshot'] }) {
  const params =
    snapshot?.params && typeof snapshot.params === 'object' && !Array.isArray(snapshot.params)
      ? Object.entries(snapshot.params as Record<string, unknown>)
      : []

  return (
    <section className="surface space-y-4 p-6">
      <div className="flex items-center gap-2.5">
        <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
          <Camera className="size-4 text-cyan" />
        </div>
        <div>
          <h2 className="text-[15px] font-medium tracking-tight text-txt">最新快照</h2>
          <p className="mt-0.5 text-[12px] text-mut2">
            三榜同源于一次快照；cutoff 之后的成交不计入这一份。
          </p>
        </div>
      </div>

      {!snapshot ? (
        <EmptyState
          icon={Camera}
          title="这一期还没有任何快照"
          description="活动开始后按配置的间隔自动跑，也可以在下方立即触发一次。"
        />
      ) : (
        <>
          {snapshot.stale && (
            <div className="flex items-start gap-2 rounded-apple bg-warn/[0.07] px-3.5 py-2.5 text-[11.5px] leading-relaxed text-warn">
              <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
              <span className="min-w-0 flex-1">
                最新快照已超过 75 分钟没有更新，前台会挂出陈旧提示。先看快照任务是否还在跑。
              </span>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="快照类型">
              <Badge
                className={cn(
                  'h-5 text-[10.5px]',
                  snapshot.kind === 'final'
                    ? 'bg-cyan/15 text-cyan hover:bg-cyan/15'
                    : 'bg-edge/12 text-txt hover:bg-edge/12',
                )}
              >
                {snapshot.kind === 'final'
                  ? '终榜'
                  : snapshot.kind === 'hourly'
                    ? '每小时'
                    : snapshot.kind}
              </Badge>
              <span className="ml-2 tnum font-mono text-[11px] text-mut2">#{snapshot.id}</span>
            </Field>
            <Field label="cutoff 区块">
              <span className="tnum font-mono text-[11.5px]">
                {formatBlock(snapshot.cutoffBlock)}
              </span>
            </Field>
            <Field label="cutoff 时间">{formatDateTime(snapshot.cutoffTime)}</Field>
            <Field label="生成时间">{formatDateTime(snapshot.createdAt)}</Field>
          </div>

          {params.length > 0 && (
            <div className="space-y-1.5 border-t border-line/50 pt-3">
              <div className="section-label">快照参数</div>
              <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-2">
                {params.map(([key, value]) => (
                  <div key={key} className="flex items-baseline justify-between gap-2">
                    <dt className="font-mono text-[11px] text-mut2">{key}</dt>
                    <dd
                      className="tnum truncate font-mono text-[11px] text-txt"
                      title={JSON.stringify(value)}
                    >
                      {JSON.stringify(value)}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </>
      )}
    </section>
  )
}

function SamplerCard({ sampler, now }: { sampler: SupernovaOverview['sampler']; now: number }) {
  const rows: Array<{ label: string; at: string | null }> = [
    { label: '最近价格采样', at: sampler.lastPriceSampleAt },
    { label: '最近汇率采样', at: sampler.lastFxSampleAt },
  ]

  return (
    <section className="surface space-y-4 p-6">
      <div className="flex items-center gap-2.5">
        <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
          <Radar className="size-4 text-cyan" />
        </div>
        <div>
          <h2 className="text-[15px] font-medium tracking-tight text-txt">采样健康</h2>
          <p className="mt-0.5 text-[12px] text-mut2">
            采样任务 15 分钟一轮，全局共用；停了市值榜与美元换算会直接失真。
          </p>
        </div>
      </div>

      <div className="space-y-2.5">
        {rows.map((row) => {
          const health = sampleHealth(row.at, now)
          return (
            <div key={row.label} className="flex items-center justify-between gap-3">
              <span className="text-[12.5px] text-txt">{row.label}</span>
              <div className="flex items-center gap-2">
                <span className="text-[11.5px] text-mut2">{formatDateTime(row.at)}</span>
                {health === 'ok' ? (
                  <Badge className="h-5 bg-green/15 text-[10.5px] text-green hover:bg-green/15">
                    正常
                  </Badge>
                ) : (
                  <Badge className="h-5 bg-warn/15 text-[10.5px] text-warn hover:bg-warn/15">
                    {health === 'stale' ? '超过 30 分钟未更新' : '从未采样'}
                  </Badge>
                )}
              </div>
            </div>
          )
        })}

        <div className="flex items-center justify-between gap-3 border-t border-line/50 pt-2.5">
          <span className="text-[12.5px] text-txt">有汇率的计价币</span>
          <span className="tnum font-mono text-[11.5px] text-mut">{sampler.fxQuotes} 个</span>
        </div>
      </div>
    </section>
  )
}

// ── 奖池解锁状态 ────────────────────────────────────────────────────────

/**
 * 阶梯奖池的解锁状态（契约 phase 14 §3 的「概览小面板」）。
 *
 * **只在阶梯模式下出现**：固定金额那一期没有"解锁"这件事，摆一块全是「—」的面板
 * 只会让人以为哪里坏了。
 *
 * 每一个数都来自 IGNIX 的 `prizePool.unlock`（后台一个数都不自己算，理由见服务层的
 * `resolvedPrizePool` 注释）。「预计 / 最终」跟着 `status` —— 在 `resultStatus` 落到
 * final 之前，这块面板上的每一个数字都还会随下一次快照变。
 */
function UnlockPanel({
  resolved,
  peakUsd,
}: {
  resolved: SupernovaOverview['resolvedPrizePool']
  /** 高水位（剔除前口径只升不降）。来自活动行，不在 `unlock` 里。 */
  peakUsd: number
}) {
  if (!resolved || resolved.mode !== 'ladder') return null

  const unlock = resolved.unlock
  const currency = resolved.currency ?? ''
  const amount = (value: number | null) =>
    value === null ? '—' : `${value.toLocaleString('en-US')} ${currency}`.trim()

  return (
    <section className="surface space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex items-center gap-2.5">
          <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
            <Trophy className="size-4 text-cyan" />
          </div>
          <div>
            <h2 className="text-[15px] font-medium tracking-tight text-txt">奖池解锁状态</h2>
            <p className="mt-0.5 text-[12px] text-mut2">
              阶梯模式：三榜金额由「活动累计有效交易额落在哪一档」派生，不落库。
            </p>
          </div>
        </div>
        {unlock && (
          <Badge
            className={cn(
              'h-5 text-[10.5px]',
              unlock.status === 'final'
                ? 'bg-cyan/15 text-cyan hover:bg-cyan/15'
                : 'bg-edge/12 text-txt hover:bg-edge/12',
            )}
          >
            {unlock.status === 'final' ? '最终' : '预计'}
          </Badge>
        )}
      </div>

      {!unlock ? (
        <p className="text-[11.5px] text-mut2">
          还没有解锁数据：这一期尚未跑过快照，或 IGNIX 暂时读不到。按第 0 档算，三榜金额为 0。
        </p>
      ) : (
        <div className="grid gap-x-4 gap-y-4 sm:grid-cols-3 lg:grid-cols-4">
          <Field label="档位依据">
            {resolved.tierBasis === 'gross' ? '剔除前（只升不降）' : '剔除后（可降）'}
            <div className="tnum mt-0.5 font-mono text-[11px] text-mut2">
              {formatUsd(unlock.basisUsd)}
            </div>
          </Field>
          <Field label="剔除前 / 剔除后">
            <span className="tnum font-mono text-[12px]">{formatUsd(unlock.grossUsd)}</span>
            <span className="text-mut2"> / </span>
            <span className="tnum font-mono text-[12px]">{formatUsd(unlock.netUsd)}</span>
          </Field>
          {/*
            高水位取活动行那一列而不是 unlock 里的：两者同源（引擎每次快照写回），
            但活动行那一份在 IGNIX 还没带 unlock 的部署期也读得到。
          */}
          <Field label="高水位">
            <span className="tnum font-mono text-[12px]">
              {formatUsd(Math.max(peakUsd, unlock.peakUsd))}
            </span>
          </Field>
          <Field label="当前档 / 总奖池">
            <span className="text-txt">第 {unlock.level} 档</span>
            <div className="tnum mt-0.5 font-mono text-[11px] text-mut">
              {amount(unlock.totalPool)}
            </div>
          </Field>
          <Field label="下一档">
            {unlock.nextThresholdUsd === null ? (
              <span className="text-mut2">已解锁最高档</span>
            ) : (
              <>
                <span className="tnum font-mono text-[12px]">
                  {formatUsd(unlock.nextThresholdUsd)}
                </span>
                <div className="tnum mt-0.5 font-mono text-[11px] text-mut2">
                  还差 {formatUsd(unlock.remainingUsd)} · 解锁 {amount(unlock.nextPoolAmount)}
                </div>
              </>
            )}
          </Field>
          {BOARDS.map((board) => (
            <Field key={board} label={`${BOARD_LABELS[board]}榜奖金`}>
              <span className="tnum font-mono text-[12px]">{amount(resolved[board])}</span>
            </Field>
          ))}
          <Field label="档内进度">
            <span className="tnum font-mono text-[12px]">
              {formatPct(Math.round(unlock.progress * 10_000) / 100)}
            </span>
          </Field>
          <Field label="数据时间">{formatDateTime(unlock.asOf)}</Field>
        </div>
      )}
    </section>
  )
}

// ── 前台曝光：当前期 ────────────────────────────────────────────────────

/**
 * 前台曝光。**只剩「设为当前期」一个动作。**
 *
 * 入口总开关从每期搬到了全局（活动列表页顶部的「全局设置」），这里只把它的当前状态
 * 摆出来并指路 —— 同名的开关摆在两处，迟早有人以为关掉的只是这一期。
 */
function ExposureSection({
  id,
  campaign,
  entryVisible,
  ask,
}: {
  id: string
  campaign: SupernovaCampaign
  /** 全局入口总开关的当前状态。读不到设置时为 null，提示退化成不带状态的那一版。 */
  entryVisible: boolean | null
  ask: (action: PendingAction) => void
}) {
  const canManage = useCan()('supernova', 'manage')
  if (!canManage) return null

  return (
    <section className="surface space-y-4 p-6">
      <div className="flex items-center gap-2.5">
        <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
          <Eye className="size-4 text-cyan" />
        </div>
        <div>
          <h2 className="text-[15px] font-medium tracking-tight text-txt">前台曝光</h2>
          <p className="mt-0.5 text-[12px] text-mut2">
            决定前台展示哪一期。<b>不影响活动本身</b>：报名、快照与榜单该怎么跑还怎么跑。
          </p>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-apple bg-edge/[0.06] px-3.5 py-2.5 text-[11.5px] leading-relaxed text-mut">
        <Info className="mt-0.5 size-3.5 shrink-0" />
        <span className="min-w-0 flex-1">
          {ENTRY_SWITCH_LABEL}在<b>全局设置</b>里（活动列表页顶部），不按期区分。
          {entryVisible === null ? (
            <>当前状态读不到，去那一块看一眼。</>
          ) : (
            <>
              当前是
              <Badge className={cn('mx-1 h-5 text-[10.5px]', entryMeta(entryVisible).className)}>
                {entryMeta(entryVisible).label}
              </Badge>
              {entryVisible
                ? '——前台展示的就是标为「当前期」的那一期。'
                : '——无论哪一期是当前期，前台都看不到入口。'}
            </>
          )}
        </span>
      </div>

      <div className="flex items-start justify-between gap-4 rounded-apple border border-line/60 p-3">
        <div className="min-w-0">
          <div className="text-[12.5px] font-medium text-txt">设为当前期</div>
          <p className="mt-0.5 text-[11px] leading-relaxed text-mut2">
            入口打开时前台展示的那一期。<b>互斥</b> —— 设这一期等于把原来的当前期取消掉。
          </p>
        </div>
        {campaign.isCurrent ? (
          <Badge className="h-6 shrink-0 bg-cyan/15 text-[11px] text-cyan hover:bg-cyan/15">
            已是当前期
          </Badge>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="pressable shrink-0 rounded-apple-sm"
            onClick={() =>
              ask({
                title: '把这一期设为当前期？',
                description:
                  '前台的活动入口会切换到这一期。同一时刻只能有一期为当前期，原来的那一期会被自动取消。',
                confirmLabel: '确认切换',
                successMessage: '已设为当前期',
                run: (reason) => api.post(`/supernova/campaigns/${id}/current`, { reason }),
              })
            }
          >
            设为当前期
          </Button>
        )}
      </div>
    </section>
  )
}

// ── 配置表单 ────────────────────────────────────────────────────────────

/**
 * 计分规则 v1.2 的四个数值参数。**标签与 hint 逐字取自 phase 6 施工契约 §4** ——
 * 这四句是运营唯一一份「这个旋钮到底干嘛的」说明书，改动之前先改契约。
 *
 * 摆成一张表而不是四段 JSX：它们的渲染、校验与提交完全同构，抄四遍只会让第五个
 * 参数加进来时漏掉其中一处。
 */
const RULE_FIELDS = [
  {
    key: 'innerVenueFactor',
    id: 'supernova-inner-venue-factor',
    label: '内盘折扣系数',
    hint: FIELD_HINTS.innerVenueFactor,
  },
  {
    key: 'minTradeUsd',
    id: 'supernova-min-trade-usd',
    label: '最小有效交易额（USD）',
    hint: FIELD_HINTS.minTradeUsd,
  },
  {
    key: 'liquidityMinUsd',
    id: 'supernova-liquidity-min-usd',
    label: '流动性门槛（USD）',
    hint: FIELD_HINTS.liquidityMinUsd,
  },
  {
    key: 'liquidityMinMcapPct',
    id: 'supernova-liquidity-min-mcap-pct',
    label: '流动性门槛（参考市值 %）',
    hint: FIELD_HINTS.liquidityMinMcapPct,
  },
  /**
   * 规则 v1.6 的第五个旋钮。摆在最后而不是挨着「最小有效交易额」：那一个是
   * **单笔**门槛（低于它的成交不计分），这一个是**累计**门槛（有效交易额不够就没有
   * 获奖资格）。两个名字只差一个字，挨着摆会让人以为是同一件事的两半。
   */
  {
    key: 'minVolumeUsd',
    id: 'supernova-min-volume-usd',
    label: '交易量榜最低有效交易额（USD）',
    hint: FIELD_HINTS.minVolumeUsd,
  },
] as const satisfies readonly { key: RuleKey; id: string; label: string; hint: string }[]

function CampaignForm({
  id,
  campaign,
  lockedFields,
  sampleIntervalMin,
  resolvedPrizePool,
}: {
  id: string
  campaign: SupernovaCampaign
  /** IGNIX 按当前时钟算出的锁定集合。它不可达时是空数组，退化成全部可编辑。 */
  lockedFields: readonly string[]
  /** 全局采样间隔。读不到时为 null，「样本数 ≈ 窗口 ÷ 间隔」那句提示退化成不带数字的版本。 */
  sampleIntervalMin: number | null
  /**
   * IGNIX 按当前快照解析出来的奖池。阶梯模式下三榜金额是**派生值**，名次区间那三张表
   * 要拿它来换算「这一段现在值多少钱」。取不到时退成只能按百分比配。
   */
  resolvedPrizePool: SupernovaOverview['resolvedPrizePool']
}) {
  const canManage = useCan()('supernova', 'manage')
  const queryClient = useQueryClient()

  /**
   * 开赛后锁住的字段。**输入框变灰只是省事，提交时剔除才是必须的** ——
   * 表单一直是整份提交的，不剔除的话一个进行中的活动每次保存都会撞
   * 409 SN_FIELD_LOCKED，连带那些本来可以改的字段（奖池、文案、快照间隔）一起写不进去。
   *
   * **在表单挂载时定格一次，置灰与剔除共用这一份。** 总览每分钟轮询一次，
   * 一个活动可能在「渲染」与「保存」之间跨进 live：若提交时按新集合剔除，
   * 运营刚在那个输入框里敲进去的值会被**静默丢掉**，界面还显示「已保存」。
   * 定格之后这种情况变成提交带上该键、由服务端回 409 —— 剔多了是静默失败，
   * 剔少了是响亮失败，两者之间永远选后者。
   *
   * 保存成功后父组件按 `campaign.updatedAt` 换 key 重挂载，这一份自然刷新。
   */
  const [locked] = useState(() => new Set(lockedFields))
  const lockedHint = '活动开始后不可修改'

  const [name, setName] = useState(campaign.name)
  const [startAt, setStartAt] = useState(toLocalInputValue(campaign.startAt))
  const [endAt, setEndAt] = useState(toLocalInputValue(campaign.endAt))
  const [intervalMin, setIntervalMin] = useState(String(campaign.snapshotIntervalMin))
  const [retentionHours, setRetentionHours] = useState(String(campaign.retentionHours))
  const [rulesVersion, setRulesVersion] = useState(campaign.rulesVersion)
  const [mcapWindowMin, setMcapWindowMin] = useState(String(campaign.mcapWindowMin))
  const [mcapSampleCount, setMcapSampleCount] = useState(String(campaign.mcapSampleCount))
  const [mcapMethod, setMcapMethod] = useState(campaign.mcapMethod)
  const [countCreatorSelfTrades, setCountCreatorSelfTrades] = useState(
    campaign.countCreatorSelfTrades,
  )
  const [countFirstBuy, setCountFirstBuy] = useState(campaign.countFirstBuy)
  const [countFounderBuys, setCountFounderBuys] = useState(campaign.countFounderBuys)
  /**
   * 计分规则 v1.2 的四个旋钮。**不在锁定集合里** —— 它们是运营旋钮，
   * 活动跑起来之后照样能改，改后下一次快照整榜按新值重算。
   */
  const [rules, setRules] = useState<RulesInput>(() => readRules(campaign.rules))
  /**
   * 奖池。**新建（库里还没有奖池）时默认落在阶梯模式**（规则 v1.6 的口径），
   * 已经配过的那一期一律按库里的 `mode` 读 —— 把一份配好的固定金额显示成阶梯，
   * 运营会以为自己的配置丢了。
   *
   * 只是切了模式、一个数都没填时，`buildPrizePool` 仍然判定成「没有奖池」并提交 null，
   * 所以这个默认值不会把一期刚建好的活动写成一份 IGNIX 会 400 的形状。
   */
  const [prizePool, setPrizePool] = useState<PrizePoolInput>(() =>
    campaign.prizePool
      ? readPrizePool(campaign.prizePool)
      : { ...EMPTY_PRIZE_POOL, mode: 'ladder', currency: V16_CURRENCY },
  )
  const [content, setContent] = useState<ContentInput>(readContent(campaign.content))
  const [reason, setReason] = useState('')
  /**
   * 上一次保存失败的原话。**留在表单里，不只弹一个几秒就消失的 toast** ——
   * IGNIX 的 `SN_BAD_PRIZE_TIERS` 会点名「哪一榜第几段、什么问题」，
   * 而那正是运营要照着去改的那一句；看完就没了的话只能反复点保存再读一遍。
   */
  const [blocked, setBlocked] = useState<string | null>(null)

  const pool = buildPrizePool(prizePool)
  const contentResult = buildContent(content)

  const save = useMutation({
    mutationFn: () => {
      const patch: Record<string, unknown> = {
        name: name.trim(),
        startAt: fromLocalInputValue(startAt),
        endAt: fromLocalInputValue(endAt),
        snapshotIntervalMin: Number(intervalMin),
        retentionHours: Number(retentionHours),
        rulesVersion: rulesVersion.trim(),
        mcapWindowMin: Number(mcapWindowMin),
        mcapSampleCount: Number(mcapSampleCount),
        mcapMethod,
        countCreatorSelfTrades,
        countFirstBuy,
        countFounderBuys,
        innerVenueFactor: Number(rules.innerVenueFactor),
        minTradeUsd: Number(rules.minTradeUsd),
        liquidityMinUsd: Number(rules.liquidityMinUsd),
        liquidityMinMcapPct: Number(rules.liquidityMinMcapPct),
        minVolumeUsd: Number(rules.minVolumeUsd),
        prizePool: pool.kind === 'value' ? pool.value : null,
        content: contentResult.kind === 'value' ? contentResult.value : null,
      }

      for (const field of locked) delete patch[field]

      return api.put(`/supernova/campaigns/${id}`, { ...patch, reason: reason.trim() })
    },
    onSuccess: () => {
      setReason('')
      setBlocked(null)
      void queryClient.invalidateQueries({ queryKey: ['supernova-overview'] })
      void queryClient.invalidateQueries({ queryKey: ['supernova-campaigns'] })
      toast.success('活动配置已保存')
    },
    onError: (error) => {
      /**
       * 锁定字段的 409 把键名放在 `data` 里。原样回显那句英文键名等于没说 ——
       * 而这条错误恰恰意味着这一层的镜像规则和 IGNIX 分叉了，运营需要看懂是哪一项。
       */
      const fields =
        error instanceof ApiError && error.errorCode === 'SN_FIELD_LOCKED'
          ? lockedFieldsFromError(error.data)
          : []

      const description =
        fields.length > 0
          ? `以下字段在当前阶段不可修改：${fields.map(fieldLabel).join('、')}`
          : errorMessage(error)

      setBlocked(description)
      toast.error('活动配置保存失败', { description })
    },
  })

  const startIso = fromLocalInputValue(startAt)
  const endIso = fromLocalInputValue(endAt)
  const rangeValid = startIso === null || endIso === null || new Date(startIso) < new Date(endIso)
  // 下限与 IGNIX 的调度频率同步：调度每 5 分钟检查一次，短于 5 分钟的设置永远不会按点触发。
  const intervalOk = inRange(intervalMin, 5, 1_440)
  // 1 小时 – 720 小时（30 天），与 IGNIX 逐字一致。
  const retentionOk = inRange(retentionHours, 1, 720)
  // 区间与 IGNIX 逐字一致：宽一格只会让人填完整张表才吃到 400。
  const windowOk = inRange(mcapWindowMin, 15, 1_440)
  /**
   * 市值样本数。**上限跟着服务端从 96 抬到 1440**（规则 v1.6 默认 144，契约 §0）——
   * 不抬的话每一期新活动的表单一打开就是红的，而运营一个字都没改。
   */
  const sampleOk = inRange(mcapSampleCount, 4, 1_440)
  // 与服务端 `campaignPatchBody.rulesVersion` 同一条正则，别让人填完整张表才被 400 弹回来。
  const versionOk = /^[A-Za-z0-9][A-Za-z0-9._-]{0,31}$/.test(rulesVersion.trim())
  // 区间与服务端 `campaignPatchBody` 逐字一致，别让人填完整张表才被 400 弹回来。
  const ruleOk = (key: RuleKey) => ruleValid(key, rules[key])
  const rulesOk = RULE_KEYS.every(ruleOk)
  const canSubmit =
    name.trim().length > 0 &&
    rangeValid &&
    intervalOk &&
    retentionOk &&
    windowOk &&
    sampleOk &&
    versionOk &&
    rulesOk &&
    pool.kind !== 'amount' &&
    // 名次区间有毛病（超出奖池、区间重叠、没填完）时保存按钮就该是灰的，
    // 而不是提交上去让 IGNIX 用 SN_BAD_PRIZE_TIERS 打回来。
    pool.kind !== 'tiers' &&
    // 阶梯与分配比例同理：IGNIX 对它们回 400 SN_BAD_PRIZE_POOL。
    pool.kind !== 'ladder' &&
    pool.kind !== 'split' &&
    contentResult.kind !== 'invalid' &&
    reasonValid(reason)

  const toggles = [
    {
      key: 'countCreatorSelfTrades',
      label: '计入发币者自买自卖',
      hint: FIELD_HINTS.countCreatorSelfTrades,
      checked: countCreatorSelfTrades,
      onChange: setCountCreatorSelfTrades,
    },
    {
      key: 'countFirstBuy',
      label: '计入开盘首买',
      hint: FIELD_HINTS.countFirstBuy,
      checked: countFirstBuy,
      onChange: setCountFirstBuy,
    },
    {
      key: 'countFounderBuys',
      label: '计入创始额度买入',
      hint: FIELD_HINTS.countFounderBuys,
      checked: countFounderBuys,
      onChange: setCountFounderBuys,
    },
  ]

  /**
   * 市值窗口里最多能有几个样本。**超过它不是错误，是浪费** —— 引擎只会拿到窗口里
   * 实际存在的那些样本，填 96 而窗口只放得下 24 个，结果和填 24 一样。
   *
   * 所以给的是黄色提示而不是红色校验：这个上限随全局采样间隔变，间隔一改大，
   * 一份本来合理的配置就会"超限"，拦下提交只会把人堵在一个不该堵的地方。
   */
  const windowMinutes = Number(mcapWindowMin)
  const sampleCap =
    sampleIntervalMin !== null && windowOk ? maxMcapSamples(windowMinutes, sampleIntervalMin) : null
  const sampleOverCap =
    sampleCap !== null && sampleOk && Number(mcapSampleCount) > sampleCap && sampleCap > 0

  /**
   * 奖池整块的可编辑性。**`resultStatus = final` 之后 IGNIX 把 `prizePool` 放进
   * `lockedFields`**（契约 §8.1）—— 锁的目的是"公布给获奖者的数字不再变"，
   * 所以锁在最终结果发布那一刻，而不是冻结那一刻。
   */
  const poolLocked = locked.has('prizePool')
  const poolEditable = canManage && !poolLocked

  /**
   * 冻结之后、发布之前是**运营复核期**：奖励分配仍然可改，而且改了立刻影响前台
   * 已经在展示的「预计奖励」。这一句就是那个提醒 —— 这一段窗口里"能改"是有意的，
   * 但改的是已经摆在用户面前的数字。
   */
  const reviewing = Boolean(campaign.frozenAt) && campaign.resultStatus !== 'final'

  /** 「平均分到三榜」。总奖池读不出数字，或三榜已经有值时不给按 —— 免得抹掉已填的数。 */
  const split = evenSplit(prizePool.total)
  const boardsEmpty =
    prizePool.mcap.trim() === '' && prizePool.volume.trim() === '' && prizePool.pnl.trim() === ''
  const canSplit = poolEditable && split !== null && split > 0

  const ladderMode = prizePool.mode === 'ladder'

  /**
   * 阶梯模式下三榜的**派生金额**：IGNIX 按当前解锁档位算好的那一份。
   *
   * 只在库里存的也是阶梯模式时才认它 —— 一期原本按固定金额配、运营刚在表单里切成阶梯时，
   * 这一份里装的还是那三个手填的旧金额，拿它当"派生值"会让界面显示一组不存在的奖金。
   */
  const derived = resolvedPrizePool?.mode === 'ladder' ? resolvedPrizePool : null
  const unlock = derived?.unlock ?? null

  /** 该榜奖池金额。换算与合计都按它算，读不出数字时是 null（金额那一列禁用）。 */
  const poolAmountOf = (board: Board): number | null => {
    if (ladderMode) {
      const amount = derived?.[board]
      return typeof amount === 'number' ? amount : null
    }
    const raw = prizePool[board].trim()
    return raw === '' || !Number.isFinite(Number(raw)) ? null : Number(raw)
  }

  const ladderIssues = ladderMode ? validateLadder(prizePool.ladder) : []
  const splitSum = splitTotal(prizePool.split)

  /**
   * 切换奖池模式。**切到阶梯时顺手把按金额配的段换算成百分比。**
   *
   * 后端定稿：阶梯模式的区间只能按百分比（`SN_BAD_PRIZE_TIERS`）。而一份原本按固定金额
   * 配好的奖池里，按金额配的段是完全正常的 —— 让运营手动把几十段逐个改成百分比，
   * 既慢又会算错；而这一步的换算基准（那三个还在表单里的固定金额）此刻恰好还在手上，
   * 切完之后就再也算不出来了。
   *
   * 换算不出来的段（该榜奖池空着）**原样留着**并由 `amountInLadder` 就地标红 ——
   * 猜一个百分比出来会静默改掉运营配的分法，而那份数字是要发给获奖者的。
   */
  const switchMode = (mode: PrizeMode) => {
    if (mode === prizePool.mode) return
    if (mode !== 'ladder') {
      setPrizePool({ ...prizePool, mode })
      return
    }

    const next = { ...prizePool.tiers }
    for (const board of BOARDS) {
      const raw = prizePool[board].trim()
      const amount = raw === '' || !Number.isFinite(Number(raw)) ? null : Number(raw)
      next[board] = toPctTiers(prizePool.tiers[board], amount)
    }
    setPrizePool({ ...prizePool, mode, tiers: next })
  }

  /** 阶梯模式下还剩几段按金额配（换算不出来的那些）。有就给一句红字。 */
  const amountRows = ladderMode
    ? BOARDS.flatMap((board) =>
        prizePool.tiers[board].filter((tier) => tier.basis === 'amount').map(() => board),
      )
    : []

  /**
   * 这一份奖池是不是还停在「什么都没配」。
   *
   * 「套用规则默认值」是**整份替换**，所以在它会覆盖掉已有配置时要先说一声 ——
   * 与「平均分到三榜」同一条口径（那一个也在三榜已有值时给黄字提示）。
   * 弹一个 confirm 不合这一页的做法：这一页所有二次确认都走 `useConfirm`，
   * 而那一套是给**写操作**用的，本地填表不该借用它。
   */
  const poolConfigured = buildPrizePool(prizePool).kind !== 'empty'

  const setTiers = (board: Board, rows: TierInput[]) => {
    setPrizePool({ ...prizePool, tiers: { ...prizePool.tiers, [board]: rows } })
  }

  /**
   * 「复制到其他两榜」。**按百分比复制** —— 三榜奖池常常不等，照搬金额会让另外两榜
   * 当场超出或严重未分完，而运营点这个按钮要的是"同样的分法"。
   */
  const copyTiers = (from: Board) => {
    /**
     * **参与奖那一行不复制。**
     *
     * 它是交易量榜特有的（规则 v1.6 只给交易量榜配参与奖池：剩下的那 30% 按有效交易额
     * 占比分给没进前十的获奖行）。盈利榜与市值榜上没有"剩余其他"这个概念 ——
     * 市值榜发的是 LP，前 N 个合格代币之外没有可分的剩余。
     *
     * 照搬过去的后果是具体的：目标榜会凭空多出一段占该榜 30% 的奖池，而合计仍然是
     * 100%，所以两张表看起来都"配好了" —— 运营要逐榜点开才看得出多了一行。
     */
    const source = toPctTiers(prizePool.tiers[from], poolAmountOf(from)).filter(
      (tier) => tierKind(tier) !== 'rest',
    )
    const next = { ...prizePool.tiers }
    for (const board of BOARDS) {
      if (board !== from) next[board] = source.map((tier) => ({ ...tier }))
    }
    setPrizePool({ ...prizePool, tiers: next })
  }

  return (
    <section className="surface space-y-5 p-6">
      <div className="flex items-center gap-2.5">
        <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
          <Timer className="size-4 text-cyan" />
        </div>
        <div>
          <h2 className="text-[15px] font-medium tracking-tight text-txt">活动配置</h2>
          <p className="mt-0.5 text-[12px] text-mut2">
            改动在下一次快照生效，已冻结的终榜不受影响。奖池只用于展示与「预计奖励」那一列，不参与排名。
          </p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <TextField
          id="supernova-name"
          label="活动名称"
          value={name}
          disabled={!canManage}
          invalid={name.trim().length === 0}
          hint={FIELD_HINTS.name}
          onChange={setName}
        />
        <div className="space-y-1.5">
          <label htmlFor="supernova-start-at" className="text-[12.5px] font-medium">
            开始时间
          </label>
          <Input
            id="supernova-start-at"
            type="datetime-local"
            value={startAt}
            disabled={!canManage || locked.has('startAt')}
            className="h-9 rounded-apple text-[13px]"
            onChange={(event) => setStartAt(event.target.value)}
          />
          {locked.has('startAt') && <p className="text-[11px] text-mut2">{lockedHint}</p>}
          <p className="text-[11px] text-mut2">{FIELD_HINTS.timeRange}</p>
        </div>
        <div className="space-y-1.5">
          <label htmlFor="supernova-end-at" className="text-[12.5px] font-medium">
            结束时间
          </label>
          <Input
            id="supernova-end-at"
            type="datetime-local"
            value={endAt}
            disabled={!canManage || locked.has('endAt')}
            aria-invalid={!rangeValid}
            className="h-9 rounded-apple text-[13px]"
            onChange={(event) => setEndAt(event.target.value)}
          />
          {locked.has('endAt') && <p className="text-[11px] text-mut2">活动结束后不可修改</p>}
        </div>
        <TextField
          id="supernova-rules-version"
          label="规则版本"
          value={rulesVersion}
          disabled={!canManage}
          locked={lockHint(locked, 'rulesVersion', lockedHint)}
          invalid={!versionOk}
          mono
          hint={FIELD_HINTS.rulesVersion}
          onChange={setRulesVersion}
        />
      </div>

      {!rangeValid && <p className="text-[11.5px] text-rose">结束时间必须晚于开始时间。</p>}

      <div className="grid gap-4 border-t border-line/50 pt-4 sm:grid-cols-2 lg:grid-cols-4">
        <TextField
          id="supernova-interval"
          label="快照间隔（分钟）"
          value={intervalMin}
          disabled={!canManage}
          invalid={!intervalOk}
          numeric
          min={5}
          hint={FIELD_HINTS.snapshotIntervalMin}
          onChange={setIntervalMin}
        />
        <TextField
          id="supernova-retention-hours"
          label="过程快照保留时长（小时）"
          value={retentionHours}
          disabled={!canManage}
          invalid={!retentionOk}
          numeric
          min={1}
          hint={FIELD_HINTS.retentionHours}
          onChange={setRetentionHours}
        />
        <TextField
          id="supernova-mcap-window"
          label="市值窗口（分钟）"
          value={mcapWindowMin}
          disabled={!canManage}
          locked={lockHint(locked, 'mcapWindowMin', lockedHint)}
          invalid={!windowOk}
          numeric
          hint={FIELD_HINTS.mcapWindowMin}
          onChange={setMcapWindowMin}
        />
        <TextField
          id="supernova-mcap-samples"
          label="市值样本数"
          value={mcapSampleCount}
          disabled={!canManage}
          locked={lockHint(locked, 'mcapSampleCount', lockedHint)}
          invalid={!sampleOk}
          numeric
          hint={mcapSampleHint(windowMinutes, sampleIntervalMin)}
          warning={sampleOverCap ? `窗口内最多只有 ${sampleCap} 个样本` : undefined}
          onChange={setMcapSampleCount}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <label htmlFor="supernova-mcap-method" className="text-[12.5px] font-medium">
            市值聚合方式
          </label>
          <Select
            value={mcapMethod}
            disabled={!canManage || locked.has('mcapMethod')}
            onValueChange={setMcapMethod}
          >
            <SelectTrigger
              id="supernova-mcap-method"
              className="h-9 w-full rounded-apple text-[13px]"
              aria-label="市值聚合方式"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-apple">
              <SelectItem value="median">中位数</SelectItem>
              <SelectItem value="mean">平均值</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[11px] text-mut2">{FIELD_HINTS.mcapMethod}</p>
        </div>
      </div>

      <div className="space-y-3 border-t border-line/50 pt-4">
        <div className="section-label">计分规则</div>

        <div className="space-y-2.5">
          {toggles.map((toggle) => (
            <div
              key={toggle.key}
              className="flex items-start justify-between gap-4 rounded-apple border border-line/60 p-3"
            >
              <div className="min-w-0">
                <div className="text-[12.5px] font-medium text-txt">{toggle.label}</div>
                <p className="mt-0.5 text-[11px] leading-relaxed text-mut2">
                  {toggle.hint}
                  {locked.has(toggle.key) && `（${lockedHint}）`}
                </p>
              </div>
              <Switch
                aria-label={toggle.label}
                checked={toggle.checked}
                disabled={!canManage || locked.has(toggle.key)}
                onCheckedChange={toggle.onChange}
              />
            </div>
          ))}
        </div>

        {/*
          这四项和上面三个开关最要紧的差别是**锁不锁**。开关一开赛就锁死；这四个数值是
          运营旋钮（规则 v1.2 §五：冷启动期第一个该上调的就是它们），活动跑起来之后照样能调。
          说清这一点比说清它们各自干嘛更要紧 —— 以为改不动的人根本不会点进来看 hint。
        */}
        <p className="text-[11px] text-mut2">
          下面四项<b>不随活动开始锁定</b>
          ：可随时改，下一次快照整榜按新值重算，已冻结的终榜不受影响。
        </p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {RULE_FIELDS.map((field) => (
            <TextField
              key={field.key}
              id={field.id}
              label={field.label}
              value={rules[field.key]}
              disabled={!canManage}
              invalid={!ruleOk(field.key)}
              numeric
              min={0}
              hint={field.hint}
              onChange={(value) => setRules((prev) => ({ ...prev, [field.key]: value }))}
            />
          ))}
        </div>
      </div>

      <div className="space-y-2.5 border-t border-line/50 pt-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="section-label">奖池</div>
            <p className="mt-1 text-[11px] text-mut2">
              金额与名次分配<b>互不牵连</b>：每一项金额都能单独留空（那一榜就没有奖），
              三榜恒各有一份名次区间配置，四项金额全空且区间没动过时整份奖池不写。
            </p>
          </div>
          {/*
            「套用规则默认值」。最新规则的那一组数字（9 档阶梯、交易量/市值/盈利 40/40/20、三榜区间与
            参与奖）手敲一遍是几十个输入框，而它们是 PRD 的口径、不是运营的判断 ——
            让人逐个敲进去，敲错一个的概率远大于零，而错的那一个会一路发到用户手里。
          */}
          <Button
            variant="outline"
            size="sm"
            className="pressable shrink-0 rounded-apple-sm"
            disabled={!poolEditable}
            title="用最新规则的默认配置整份替换当前表单：切到阶梯解锁、9 档阶梯、交易量/市值/盈利 40/40/20 分配、三榜名次区间与交易量榜参与奖、币种 USDG。不会自动保存。"
            onClick={() => setPrizePool(v16PrizePool())}
          >
            套用规则默认值
          </Button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <label htmlFor="supernova-pool-mode" className="text-[12.5px] font-medium">
              奖池模式
            </label>
            <Select
              value={prizePool.mode}
              disabled={!poolEditable}
              onValueChange={(value) => switchMode(value as PrizeMode)}
            >
              <SelectTrigger
                id="supernova-pool-mode"
                className="h-9 w-full rounded-apple text-[13px]"
                aria-label="奖池模式"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-apple">
                {PRIZE_MODES.map((mode) => (
                  <SelectItem key={mode} value={mode}>
                    {PRIZE_MODE_LABELS[mode]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-mut2">{FIELD_HINTS.prizeMode}</p>
          </div>
          <TextField
            id="supernova-pool-currency"
            label="计价币种"
            value={prizePool.currency}
            disabled={!poolEditable}
            invalid={!/^[A-Z]{1,8}$/.test(prizePool.currency.trim())}
            mono
            hint="1–8 位大写字母，进前台的奖金单位。阶梯模式默认 USDG。"
            onChange={(value) => setPrizePool({ ...prizePool, currency: value.toUpperCase() })}
          />
          {ladderMode && (
            <div className="space-y-1.5 sm:col-span-2">
              <label htmlFor="supernova-pool-tier-basis" className="text-[12.5px] font-medium">
                档位依据
              </label>
              <Select
                value={prizePool.tierBasis}
                disabled={!poolEditable}
                onValueChange={(value) =>
                  setPrizePool({ ...prizePool, tierBasis: value as TierBasisMode })
                }
              >
                <SelectTrigger
                  id="supernova-pool-tier-basis"
                  className="h-9 w-full rounded-apple text-[13px]"
                  aria-label="档位依据"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-apple">
                  {TIER_BASIS_MODES.map((mode) => (
                    <SelectItem key={mode} value={mode}>
                      {TIER_BASIS_LABELS[mode]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-mut2">{FIELD_HINTS.prizeTierBasis}</p>
            </div>
          )}
        </div>

        {poolConfigured && (
          <p className="text-[11px] text-warn">
            已经配过的奖池：点「套用规则默认值」会把模式、阶梯、分配比例与三榜区间整份覆盖，保存前可以再改。
          </p>
        )}

        {ladderMode && (
          <>
            <LadderTable
              rows={prizePool.ladder}
              currency={prizePool.currency.trim() || 'USD'}
              issues={ladderIssues}
              disabled={!poolEditable}
              onChange={(rows) => setPrizePool({ ...prizePool, ladder: rows })}
            />

            <div className="space-y-1.5 rounded-apple border border-line/60 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-[12.5px] font-medium text-txt">三榜分配比例（%）</div>
                <span
                  className={cn(
                    'tnum text-[11.5px]',
                    splitSum === null ? 'text-mut2' : splitValid(prizePool.split) ? 'text-mut2' : 'text-rose',
                  )}
                >
                  {splitSum === null ? '三项都要填' : `合计 ${formatPct(splitSum)}`}
                </span>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                {BOARDS.map((board) => (
                  <TextField
                    key={board}
                    id={`supernova-pool-split-${board}`}
                    label={`${BOARD_LABELS[board]}榜`}
                    value={prizePool.split[board]}
                    disabled={!poolEditable}
                    invalid={prizePool.split[board].trim() !== '' && splitSum !== null && !splitValid(prizePool.split)}
                    numeric
                    onChange={(value) =>
                      setPrizePool({
                        ...prizePool,
                        split: { ...prizePool.split, [board]: value },
                      })
                    }
                  />
                ))}
              </div>
              <p className="text-[11px] text-mut2">{FIELD_HINTS.prizeSplit}</p>
            </div>

            {pool.kind === 'split' && (
              <p className="text-[11.5px] text-rose">三榜分配比例必须都填，且合计正好 100。</p>
            )}

            {/*
              阶梯模式下三榜金额是派生值，所以这里摆的是「现在解锁到哪一档」——
              没有它的话，下面那三张表里的金额列会显示一组来历不明的数字。
            */}
            <UnlockLine unlock={unlock} currency={derived?.currency ?? prizePool.currency} />
          </>
        )}
        {/*
          固定金额那四个框**只在固定模式下出现**：阶梯模式下它们由档位派生、不落库，
          摆在界面上会让人以为填了有用，而 IGNIX 收到它们会直接 400。
        */}
        {!ladderMode && (
          <>
            <div className="flex flex-wrap items-center justify-end gap-3">
              {/*
                「填了总奖池，三榜还空着」是最常见的一步。让人自己心算 total/3 再敲三遍，
                既慢又容易把 10000/3 敲成三个 3333.33 —— 公示的数字带小数分位看起来像算错了。
              */}
              <Button
                variant="outline"
                size="sm"
                className="pressable shrink-0 rounded-apple-sm"
                disabled={!canSplit}
                title={
                  canSplit
                    ? `每榜填 ${split}（总奖池 ÷ 3，向下取整到 1 USD）`
                    : '先在总奖池里填一个大于 0 的数字'
                }
                onClick={() =>
                  split !== null &&
                  setPrizePool({
                    ...prizePool,
                    mcap: String(split),
                    volume: String(split),
                    pnl: String(split),
                  })
                }
              >
                平均分到三榜
              </Button>
            </div>
            {canSplit && !boardsEmpty && (
              <p className="text-[11px] text-warn">
                三榜已经有值，点「平均分到三榜」会覆盖它们。
              </p>
            )}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {(
                [
                  { key: 'total', label: '总奖池', hint: FIELD_HINTS.prizeTotal },
                  { key: 'mcap', label: '市值榜奖池', hint: FIELD_HINTS.prizeBoard },
                  { key: 'volume', label: '交易量榜奖池', hint: FIELD_HINTS.prizeBoard },
                  { key: 'pnl', label: '盈亏榜奖池', hint: FIELD_HINTS.prizeBoard },
                ] as const
              ).map((item) => (
                <TextField
                  key={item.key}
                  id={`supernova-pool-${item.key}`}
                  label={item.label}
                  value={prizePool[item.key]}
                  disabled={!poolEditable}
                  numeric
                  hint={item.hint}
                  onChange={(value) => setPrizePool({ ...prizePool, [item.key]: value })}
                />
              ))}
            </div>
            {pool.kind === 'amount' && (
              <p className="text-[11.5px] text-rose">金额只能填非负数字，或者留空。</p>
            )}
          </>
        )}
        {poolLocked && (
          <p className="text-[11px] text-mut2">
            最终结果已发布，奖池与名次奖励一起锁定 —— 公布给获奖者的数字不再改动。
          </p>
        )}
        {!poolLocked && reviewing && (
          <p className="flex items-start gap-1.5 text-[11.5px] text-warn">
            <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
            <span>终榜已冻结，改动会直接影响公布的预计奖励。</span>
          </p>
        )}
        <div className="space-y-4 pt-1">
          <p className="text-[11px] text-mut2">{FIELD_HINTS.prizeTiers}</p>
          <p className="text-[11px] text-mut2">{FIELD_HINTS.prizeRest}</p>
          {ladderMode && (
            <p className="text-[11px] text-mut2">
              阶梯模式下金额列<b>按当前档位派生、只读</b>：区间只能按百分比配（切过来时
              已按原奖池自动换算）。
              {unlock === null && ' 还没跑过快照（或档位未解锁），所以金额列暂时换算不出来。'}
            </p>
          )}
          {amountRows.length > 0 && (
            <p className="flex items-start gap-1.5 text-[11.5px] text-rose">
              <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
              <span>
                还有 {amountRows.length} 段按金额配（
                {[...new Set(amountRows)].map((board) => BOARD_LABELS[board]).join('、')}
                榜）：切过来时该榜奖池是空的，换算不出百分比。请在那几段的「百分比」列里直接填。
              </span>
            </p>
          )}
          {BOARDS.map((board) => (
            <TierTable
              key={board}
              board={board}
              tiers={prizePool.tiers[board]}
              poolAmount={poolAmountOf(board)}
              amountsDerived={ladderMode}
              currency={prizePool.currency.trim() || 'USD'}
              disabled={!poolEditable}
              onChange={(rows) => setTiers(board, rows)}
              onCopy={() => copyTiers(board)}
            />
          ))}
        </div>
      </div>

      <div className="space-y-2.5 border-t border-line/50 pt-4">
        <div>
          <div className="section-label">前台文案覆盖</div>
          <p className="mt-1 text-[11px] text-mut2">{FIELD_HINTS.content}</p>
          <p className="mt-1 text-[11px] text-mut2">
            三个链接必须是完整的 https 地址：http 会被浏览器拦成混合内容，
            没有协议的写法则会落到站内的一个 404。
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="supernova-content-title"
            label="主标题"
            value={content.title}
            disabled={!canManage}
            onChange={(value) => setContent({ ...content, title: value })}
          />
          <TextField
            id="supernova-content-subtitle"
            label="副标题"
            value={content.subtitle}
            disabled={!canManage}
            onChange={(value) => setContent({ ...content, subtitle: value })}
          />
          {(
            [
              { key: 'metaagents', label: 'Metagents 链接' },
              { key: 'okxai', label: 'OKX.AI 链接' },
              { key: 'tg', label: 'Telegram 链接' },
            ] as const
          ).map((item) => (
            <TextField
              key={item.key}
              id={`supernova-content-${item.key}`}
              label={item.label}
              value={content[item.key]}
              disabled={!canManage}
              invalid={contentResult.kind === 'invalid' && contentResult.field === item.key}
              placeholder="https://"
              onChange={(value) => setContent({ ...content, [item.key]: value })}
            />
          ))}
        </div>
        {contentResult.kind === 'invalid' && (
          <p className="text-[11.5px] text-rose">链接必须是完整的 https 地址。</p>
        )}
      </div>

      {canManage ? (
        <div className="space-y-3 border-t border-line/50 pt-4">
          <ReasonField
            id="supernova-campaign-reason"
            value={reason}
            onChange={setReason}
            disabled={save.isPending}
          />
          {blocked && (
            <div className="flex items-start gap-2 rounded-apple bg-warn/[0.07] px-3.5 py-2.5 text-[11.5px] leading-relaxed text-warn">
              <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
              <span className="min-w-0 flex-1">{blocked}</span>
            </div>
          )}
          <div className="flex justify-end">
            <Button
              className="pressable rounded-apple"
              disabled={!canSubmit || save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? '保存中…' : '保存配置'}
            </Button>
          </div>
        </div>
      ) : (
        <p className="border-t border-line/50 pt-4 text-[11.5px] text-mut2">
          只读视图。修改活动配置需要 SuperNova 管理权限。
        </p>
      )}
    </section>
  )
}

function inRange(value: string, min: number, max: number): boolean {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed >= min && parsed <= max
}

function TextField({
  id,
  label,
  value,
  disabled,
  locked,
  invalid,
  numeric,
  min,
  mono,
  placeholder,
  hint,
  warning,
  onChange,
}: {
  id: string
  label: string
  value: string
  disabled?: boolean
  /** 非空时这一项被阶段锁住：输入框变灰，并把原因摆在下面。 */
  locked?: string
  invalid?: boolean
  numeric?: boolean
  min?: number
  mono?: boolean
  placeholder?: string
  /**
   * 常驻在输入框下面的说明，与 `locked` 不冲突时同时可能出现两句 ——
   * 锁定原因说"现在为什么不能改"，这一句说"这个字段本身的规则"，两者答的是不同问题。
   */
  hint?: string
  /**
   * 黄色的软提醒。**与 `invalid` 是两件事**：`invalid` 说"这个值不合法、提交会被拒"，
   * 这一句说"这个值合法，但大概率不是你想要的"。混成一个的话，要么把一个能提交的
   * 配置拦住，要么把一句要紧的提醒降成灰字。
   */
  warning?: string
  onChange: (value: string) => void
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-[12.5px] font-medium">
        {label}
      </label>
      <Input
        id={id}
        value={value}
        disabled={disabled || Boolean(locked)}
        placeholder={placeholder}
        inputMode={numeric ? 'decimal' : undefined}
        min={min}
        aria-invalid={invalid}
        className={cn('h-9 rounded-apple text-[13px]', mono && 'font-mono')}
        onChange={(event) => onChange(event.target.value)}
      />
      {locked && <p className="text-[11px] text-mut2">{locked}</p>}
      {warning && <p className="text-[11px] text-warn">{warning}</p>}
      {hint && <p className="text-[11px] text-mut2">{hint}</p>}
    </div>
  )
}

/** 阶段锁住某一项时给的那句话。集中在一处，免得十来个输入框各写各的。 */
function lockHint(locked: Set<string>, field: string, hint: string): string | undefined {
  return locked.has(field) ? hint : undefined
}

// ── 名次奖励编辑器 ──────────────────────────────────────────────────────

/** 换算出来的那一列。最多两位小数，尾随的 0 去掉 —— 它进的是输入框，不是报表。 */
function derivedText(value: number | null): string {
  return value === null ? '' : String(Math.round(value * 100) / 100)
}

/**
 * 一榜的名次奖励编辑器（契约 phase 9 §3）。**三榜各一张，共用这一个组件。**
 *
 * 百分比与金额**任填一个**：被编辑的那一列就是 `basis`，另一列按该榜奖池现算出来、
 * 灰着显示。改另一列则 basis 跟着切换 —— 两列都当成输入存下来的话，
 * 「奖池金额改了」就没有确定的含义（哪一列该跟着缩放？）。
 *
 * 校验与合计都走 `-shared` 那一份（与 IGNIX 同源），这一层只负责把结果摆出来：
 * 挂在某一段上的毛病就地标红，合计类的（超出奖池 / 段数超限）摆在底部那一行。
 */
function TierTable({
  board,
  tiers,
  poolAmount,
  amountsDerived,
  currency,
  disabled,
  onChange,
  onCopy,
}: {
  board: Board
  tiers: TierInput[]
  /** 该榜奖池金额。null 表示没填 —— 金额那一列整列禁用（换算不出来）。 */
  poolAmount: number | null
  /**
   * 金额是不是**派生**出来的（阶梯模式）。派生时整列只读：那一列的数跟着解锁档位变，
   * 在它上面输入等于把一个会自己变的数当成配置，而下一次快照就把它冲掉了。
   */
  amountsDerived: boolean
  currency: string
  disabled: boolean
  onChange: (rows: TierInput[]) => void
  onCopy: () => void
}) {
  const label = BOARD_LABELS[board]
  const issues = validateTiers(tiers, poolAmount, { ladder: amountsDerived })
  const summary = summarizeTiers(tiers, poolAmount)
  /** 奖池金额空着或是 0 时金额那一列换算不出来，整列禁用 —— IGNIX 对它直接 400。 */
  const byAmount = poolUsable(poolAmount)
  const hasRest = tiers.some((tier) => tierKind(tier) === 'rest')
  const tableIssues = issues.filter((issue): issue is Exclude<TierIssue, { index: number }> =>
    !isRowIssue(issue),
  )

  const patch = (index: number, next: Partial<TierInput>) =>
    onChange(tiers.map((tier, position) => (position === index ? { ...tier, ...next } : tier)))

  /** 加一段默认接着上一段往下排：运营几乎总是在配一串连续的名次区间。 */
  const end = Number(tiers[tiers.length - 1]?.to.trim())
  const nextFrom = Number.isInteger(end) && end >= 1 ? end + 1 : 1

  const add = () =>
    onChange([
      ...tiers,
      { from: String(nextFrom), to: String(nextFrom), mode: 'each', basis: 'pct', value: '' },
    ])

  /**
   * 加参与奖那一行。**追加在最后**：它拿的是「名次配完之后剩下的」，摆在名次区间中间
   * 会让人以为它也占一段名次。每榜至多一条，已有时按钮就该是灰的。
   */
  const addRest = () => onChange([...tiers, restTier()])

  /**
   * 段数到顶、或上一段已经排到最后一个名次时不给加 —— 加出来的那一段当场就是红的，
   * 而运营真正需要知道的是"到顶了"，不是"你刚加的这段不合法"。
   */
  const addBlocked = tiers.length >= TIER_MAX_ROWS || nextFrom > TIER_MAX_RANK

  const remove = (index: number) => onChange(tiers.filter((_, position) => position !== index))

  const cell = 'px-2 py-1.5'

  return (
    <div className="space-y-1.5 rounded-apple border border-line/60 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[12.5px] font-medium text-txt">
          {label}榜奖池{' '}
          <span className="tnum text-mut2">
            {poolAmount === null ? '未填' : formatUsd(poolAmount)}
          </span>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            variant="outline"
            size="sm"
            className="pressable rounded-apple-sm"
            disabled={disabled}
            aria-label={`${label}榜 前三 50/30/20`}
            onClick={() => onChange(defaultTiers())}
          >
            前三 50/30/20
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="pressable rounded-apple-sm"
            disabled={disabled}
            aria-label={`${label}榜 复制到其他两榜`}
            title="按百分比复制过去，金额按各榜奖池重算；参与奖池不会复制"
            onClick={onCopy}
          >
            复制到其他两榜
          </Button>
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className={cn('section-label', cell)}>名次</TableHead>
            <TableHead className={cn('section-label', cell)}>分配方式</TableHead>
            <TableHead className={cn('section-label', cell)}>百分比（%）</TableHead>
            <TableHead className={cn('section-label', cell)}>金额（USD）</TableHead>
            <TableHead className={cn('section-label text-right', cell)}>每名</TableHead>
            <TableHead className={cn('section-label', cell)} />
          </TableRow>
        </TableHeader>
        <TableBody>
          {tiers.length === 0 && (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={6} className={cn('text-[11.5px] text-mut2', cell)}>
                一段都没有 = 这一榜不发奖。要回到默认的前三分配，点上面的「前三 50/30/20」。
              </TableCell>
            </TableRow>
          )}

          {tiers.map((tier, index) => {
            const calc = tierCalc(tier, poolAmount)
            const rowIssues = issues.filter((issue) => isRowIssue(issue) && issue.index === index)
            const rankBad = rowIssues.some(
              (issue) =>
                issue.code === 'rank' || issue.code === 'overlap' || issue.code === 'order',
            )
            const valueBad = rowIssues.some(
              (issue) =>
                issue.code === 'blank' ||
                issue.code === 'value' ||
                issue.code === 'amountNoPool' ||
                issue.code === 'amountInLadder',
            )
            const rest = tierKind(tier) === 'rest'
            const name = rest ? `${label}榜参与奖` : `${label}榜第 ${index + 1} 段`

            return (
              <Fragment key={index}>
                <TableRow
                  className={cn('border-line/40', rowIssues.length > 0 && 'bg-rose/[0.06]')}
                >
                  <TableCell className={cell}>
                    {rest ? (
                      <Badge className="h-5 bg-cyan/15 text-[10.5px] text-cyan hover:bg-cyan/15">
                        剩余其他
                      </Badge>
                    ) : (
                      <div className="flex items-center gap-1">
                        <Input
                          aria-label={`${name}起始名次`}
                          aria-invalid={rankBad}
                          value={tier.from}
                          disabled={disabled}
                          inputMode="numeric"
                          className="h-8 w-14 rounded-apple text-center text-[12px]"
                          onChange={(event) => patch(index, { from: event.target.value })}
                        />
                        <span className="text-mut2">–</span>
                        <Input
                          aria-label={`${name}结束名次`}
                          aria-invalid={rankBad}
                          value={tier.to}
                          disabled={disabled}
                          inputMode="numeric"
                          className="h-8 w-14 rounded-apple text-center text-[12px]"
                          onChange={(event) => patch(index, { to: event.target.value })}
                        />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className={cell}>
                    {rest ? (
                      <Select
                        value={tier.split ?? 'pro_rata'}
                        disabled={disabled}
                        onValueChange={(value) => patch(index, { split: value as TierSplit })}
                      >
                        <SelectTrigger
                          aria-label={`${name}分法`}
                          className="h-8 w-28 rounded-apple text-[12px]"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-apple">
                          {TIER_SPLITS.map((split) => (
                            <SelectItem key={split} value={split}>
                              {TIER_SPLIT_LABELS[split]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Select
                        value={tier.mode}
                        disabled={disabled}
                        onValueChange={(value) => patch(index, { mode: value as TierMode })}
                      >
                        <SelectTrigger
                          aria-label={`${name}分配方式`}
                          className="h-8 w-28 rounded-apple text-[12px]"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-apple">
                          {TIER_MODES.map((mode) => (
                            <SelectItem key={mode} value={mode}>
                              {TIER_MODE_LABELS[mode]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell className={cell}>
                    <Input
                      aria-label={`${name}百分比`}
                      aria-invalid={valueBad && tier.basis === 'pct'}
                      value={tier.basis === 'pct' ? tier.value : derivedText(calc.pct)}
                      disabled={disabled}
                      inputMode="decimal"
                      className={cn(
                        'h-8 w-20 rounded-apple text-[12px]',
                        tier.basis !== 'pct' && 'text-mut2',
                      )}
                      onChange={(event) =>
                        patch(index, { basis: 'pct', value: event.target.value })
                      }
                    />
                  </TableCell>
                  <TableCell className={cell}>
                    <Input
                      aria-label={`${name}金额`}
                      aria-invalid={valueBad && tier.basis === 'amount'}
                      value={tier.basis === 'amount' ? tier.value : derivedText(calc.amount)}
                      disabled={disabled || !byAmount || amountsDerived}
                      readOnly={amountsDerived}
                      title={amountsDerived ? '阶梯模式下金额按当前解锁档位派生' : undefined}
                      inputMode="decimal"
                      className={cn(
                        'h-8 w-24 rounded-apple text-[12px]',
                        tier.basis !== 'amount' && 'text-mut2',
                      )}
                      onChange={(event) =>
                        patch(index, { basis: 'amount', value: event.target.value })
                      }
                    />
                  </TableCell>
                  <TableCell className={cn('tnum text-right text-[12px] text-txt', cell)}>
                    {calc.perRankAmount === null
                      ? calc.pct === null || calc.ranks === null
                        ? '—'
                        : formatPct(tier.mode === 'each' ? calc.pct : calc.pct / calc.ranks)
                      : formatUsd(calc.perRankAmount)}
                  </TableCell>
                  <TableCell className={cn('text-right', cell)}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 rounded-apple-sm text-mut2 hover:text-rose"
                      disabled={disabled}
                      aria-label={`删除${name}`}
                      onClick={() => remove(index)}
                    >
                      <X className="size-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>

                {/*
                  参与奖那两个可选项摆在下一行而不是挤进表头：它们只对这一行有意义，
                  给整张表加两列会让另外几十段名次各多两个永远空着的框。
                */}
                {rest && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={6} className={cn('pt-0', cell)}>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        <label className="flex items-center gap-2 text-[11.5px] text-mut">
                          参与门槛（{currency}）
                          <Input
                            aria-label={`${name}参与门槛`}
                            aria-invalid={rowIssues.some((issue) => issue.code === 'minMetric')}
                            value={tier.minMetricUsd ?? ''}
                            disabled={disabled}
                            inputMode="decimal"
                            placeholder="不设"
                            className="h-8 w-24 rounded-apple text-[12px]"
                            onChange={(event) =>
                              patch(index, { minMetricUsd: event.target.value })
                            }
                          />
                        </label>
                        <label className="flex items-center gap-2 text-[11.5px] text-mut">
                          单人上限（占参与奖池 %）
                          <Input
                            aria-label={`${name}单人上限`}
                            aria-invalid={rowIssues.some((issue) => issue.code === 'cap')}
                            value={tier.capPct ?? ''}
                            disabled={disabled}
                            inputMode="decimal"
                            placeholder="不封顶"
                            className="h-8 w-20 rounded-apple text-[12px]"
                            onChange={(event) => patch(index, { capPct: event.target.value })}
                          />
                        </label>
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {rowIssues.length > 0 && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={6} className={cn('pt-0 text-[11px] text-rose', cell)}>
                      {rowIssues.map((issue) => tierIssueMessage(issue)).join('；')}
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            )
          })}
        </TableBody>
      </Table>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="pressable rounded-apple-sm text-[12px]"
            disabled={disabled || addBlocked}
            aria-label={`${label}榜加一段`}
            title={
              addBlocked
                ? `最多 ${TIER_MAX_ROWS} 段，且名次最深到第 ${TIER_MAX_RANK} 名`
                : `从第 ${nextFrom} 名接着排`
            }
            onClick={add}
          >
            <Plus className="size-3.5" />
            加一段
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="pressable rounded-apple-sm text-[12px]"
            disabled={disabled || hasRest || tiers.length >= TIER_MAX_ROWS}
            aria-label={`${label}榜加参与奖`}
            title={
              hasRest
                ? '每榜最多一条「剩余其他」'
                : '不占名次，分给所有没被名次区间覆盖到的获奖行'
            }
            onClick={addRest}
          >
            <Plus className="size-3.5" />
            加参与奖
          </Button>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-x-2 text-[11.5px]">
          <span className="tnum text-mut2">{tierSummaryText(summary)}</span>
          {tableIssues.map((issue) => (
            <span key={issue.code} className="text-rose">
              {tierIssueMessage(issue)}
            </span>
          ))}
          {/*
            未分完**不拦**（契约 §2.2 第 2 条）：运营可能故意留一部分做人工奖。
            所以它是黄字而不是红字，保存按钮照样能点。
          */}
          {tableIssues.length === 0 && summary.rows > 0 && summary.remainingPct > 0 && (
            <span className="text-warn">未分完，剩下的不进预计奖励</span>
          )}
        </div>
      </div>

      {!byAmount && (
        <p className="text-[11px] text-mut2">
          {amountsDerived
            ? '当前档位还没解锁出这一榜的奖池金额，所以只能按百分比配。'
            : '先填一个大于 0 的该榜奖池金额，才能按金额配。'}
        </p>
      )}
    </div>
  )
}

/**
 * 阶梯档位编辑器（契约 phase 14 §3）。
 *
 * 阈值与总奖池两列，行可增删。**校验在 `-shared` 那一份**（与 IGNIX 同源）：
 * 严格递增、≤ 20 档、非负、至多两位小数；挂在某一档上的毛病就地标红，
 * 整表类的（一档都没有 / 档数超限）摆在底部那一行。
 */
function LadderTable({
  rows,
  currency,
  issues,
  disabled,
  onChange,
}: {
  rows: LadderInput[]
  currency: string
  issues: readonly LadderIssue[]
  disabled: boolean
  onChange: (rows: LadderInput[]) => void
}) {
  const cell = 'px-2 py-1.5'
  const tableIssues = issues.filter((issue) => !('index' in issue))

  const patch = (index: number, next: Partial<LadderInput>) =>
    onChange(rows.map((row, position) => (position === index ? { ...row, ...next } : row)))

  const remove = (index: number) => onChange(rows.filter((_, position) => position !== index))

  /** 加一档默认接着上一档：运营几乎总是从低往高排，而阈值必须严格递增。 */
  const add = () => onChange([...rows, { thresholdUsd: '', poolAmount: '' }])

  return (
    <div className="space-y-1.5 rounded-apple border border-line/60 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[12.5px] font-medium text-txt">
          解锁阶梯 <span className="tnum text-mut2">{rows.length} 档</span>
        </div>
        <span className="text-[11px] text-mut2">{FIELD_HINTS.prizeLadder}</span>
      </div>

      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className={cn('section-label w-16', cell)}>档位</TableHead>
            <TableHead className={cn('section-label', cell)}>累计有效交易额（USD）</TableHead>
            <TableHead className={cn('section-label', cell)}>总奖池（{currency}）</TableHead>
            <TableHead className={cn('section-label', cell)} />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={4} className={cn('text-[11.5px] text-mut2', cell)}>
                一档都没有。阶梯模式至少要有一档 —— 要整套规则文档的默认档位，点上面的「套用规则默认值」。
              </TableCell>
            </TableRow>
          )}

          {rows.map((row, index) => {
            const rowIssues = issues.filter(
              (issue): issue is Extract<LadderIssue, { index: number }> =>
                'index' in issue && issue.index === index,
            )

            return (
              <Fragment key={index}>
                <TableRow
                  className={cn('border-line/40', rowIssues.length > 0 && 'bg-rose/[0.06]')}
                >
                  <TableCell className={cn('tnum font-mono text-[12px] text-mut', cell)}>
                    第 {index + 1} 档
                  </TableCell>
                  <TableCell className={cell}>
                    <Input
                      aria-label={`第 ${index + 1} 档解锁阈值`}
                      aria-invalid={rowIssues.length > 0}
                      value={row.thresholdUsd}
                      disabled={disabled}
                      inputMode="decimal"
                      className="h-8 w-36 rounded-apple text-[12px]"
                      onChange={(event) => patch(index, { thresholdUsd: event.target.value })}
                    />
                  </TableCell>
                  <TableCell className={cell}>
                    <Input
                      aria-label={`第 ${index + 1} 档总奖池`}
                      aria-invalid={rowIssues.length > 0}
                      value={row.poolAmount}
                      disabled={disabled}
                      inputMode="decimal"
                      className="h-8 w-32 rounded-apple text-[12px]"
                      onChange={(event) => patch(index, { poolAmount: event.target.value })}
                    />
                  </TableCell>
                  <TableCell className={cn('text-right', cell)}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 rounded-apple-sm text-mut2 hover:text-rose"
                      disabled={disabled}
                      aria-label={`删除第 ${index + 1} 档`}
                      onClick={() => remove(index)}
                    >
                      <X className="size-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>

                {rowIssues.length > 0 && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={4} className={cn('pt-0 text-[11px] text-rose', cell)}>
                      {rowIssues.map((issue) => ladderIssueMessage(issue)).join('；')}
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            )
          })}
        </TableBody>
      </Table>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="pressable rounded-apple-sm text-[12px]"
          disabled={disabled || rows.length >= LADDER_MAX_ROWS}
          aria-label="加一档"
          title={rows.length >= LADDER_MAX_ROWS ? `最多 ${LADDER_MAX_ROWS} 档` : '接着往上排'}
          onClick={add}
        >
          <Plus className="size-3.5" />
          加一档
        </Button>
        <div className="flex flex-wrap items-center justify-end gap-x-2 text-[11.5px]">
          {tableIssues.map((issue) => (
            <span key={issue.code} className="text-rose">
              {ladderIssueMessage(issue)}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * 「现在解锁到哪一档」的一行摘要。配置表单里用它，概览那一块用 `UnlockPanel`。
 *
 * 取不到解锁状态（还没跑过快照、或 IGNIX 不可达）时说的是「还没有解锁数据」而不是
 * 「第 0 档」—— 后者看起来像一个已经算过的结论，而实际上是压根还没算。
 */
function UnlockLine({
  unlock,
  currency,
}: {
  unlock: SupernovaUnlock | null
  currency: string | null
}) {
  if (!unlock) {
    return (
      <p className="text-[11px] text-mut2">
        还没有解锁数据（活动尚未跑过快照，或 IGNIX 暂时读不到），三榜金额按第 0 档算。
      </p>
    )
  }

  return (
    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-mut">
      <span>
        当前 <b className="text-txt">第 {unlock.level} 档</b>
      </span>
      <span>
        总奖池{' '}
        <span className="tnum font-mono text-txt">
          {unlock.totalPool.toLocaleString('en-US')} {currency ?? ''}
        </span>
      </span>
      <span>
        依据 <span className="tnum font-mono text-txt">{formatUsd(unlock.basisUsd)}</span>
      </span>
      <Badge
        className={cn(
          'h-5 text-[10.5px]',
          unlock.status === 'final'
            ? 'bg-cyan/15 text-cyan hover:bg-cyan/15'
            : 'bg-edge/12 text-txt hover:bg-edge/12',
        )}
      >
        {unlock.status === 'final' ? '最终' : '预计'}
      </Badge>
    </p>
  )
}

// ── 操作区：快照 / 冻结 / 结果状态 ──────────────────────────────────────

function ActionsSection({
  id,
  data,
  ask,
}: {
  id: string
  data: SupernovaOverview
  ask: (action: PendingAction) => void
}) {
  const canManage = useCan()('supernova', 'manage')
  if (!canManage) return null

  const { campaign } = data
  const frozen = Boolean(campaign.frozenAt)

  return (
    <section className="surface space-y-4 p-6">
      <div className="flex items-center gap-2.5">
        <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
          <Snowflake className="size-4 text-cyan" />
        </div>
        <div>
          <h2 className="text-[15px] font-medium tracking-tight text-txt">活动操作</h2>
          <p className="mt-0.5 text-[12px] text-mut2">
            全部写审计。冻结之后过程快照任务对这一期直接退出，终榜不再变化。
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2.5">
        <Button
          variant="outline"
          className="pressable rounded-apple"
          onClick={() =>
            ask({
              title: '立即跑一次快照？',
              description:
                '按当前配置与索引进度算一份新的三榜。cutoff 由服务端定，不会超过索引游标——这不是「重算历史」，已生成的快照不受影响。',
              confirmLabel: '确认快照',
              successMessage: '已触发一次快照',
              invalidate: ['supernova-overview', 'supernova-leaderboard'],
              run: (reason) => api.post(`/supernova/campaigns/${id}/snapshot`, { reason }),
            })
          }
        >
          <Camera className="size-3.5" />
          立即快照
        </Button>

        <Button
          variant="destructive"
          className="pressable rounded-apple"
          disabled={frozen}
          onClick={() =>
            ask({
              title: '确认冻结终榜？',
              description:
                '冻结会按 endBlock 生成一份 final 快照并写入冻结时间，结果状态自动变为「终榜待审核」。之后过程快照任务直接退出，榜单不再更新，后台没有解冻入口。请先确认索引已经追过活动结束区块。',
              confirmLabel: '确认冻结',
              destructive: true,
              successMessage: '终榜已冻结',
              invalidate: ['supernova-overview', 'supernova-leaderboard'],
              run: (reason) => api.post(`/supernova/campaigns/${id}/freeze`, { reason }),
            })
          }
        >
          <Snowflake className="size-3.5" />
          {frozen ? '终榜已冻结' : '冻结终榜'}
        </Button>
      </div>

      <div className="space-y-2.5 border-t border-line/50 pt-4">
        <div className="flex items-center gap-2">
          <Gavel className="size-3.5 text-mut" />
          <div className="section-label">结果状态</div>
        </div>
        <p className="text-[11px] leading-relaxed text-mut2">
          冻结之后是「终榜待审核」。人工核对完名单与递补名次，再置为「最终结果」——
          前台会据此把奖项与预计奖励高亮出来，等于对外宣布名次定稿。
        </p>

        <div className="flex flex-wrap items-center gap-2.5">
          <Badge
            className={cn(
              'h-6 text-[11px]',
              RESULT_STATUS_META[campaign.resultStatus]?.className ??
                'bg-edge/10 text-mut hover:bg-edge/10',
            )}
          >
            当前：{RESULT_STATUS_META[campaign.resultStatus]?.label ?? campaign.resultStatus}
          </Badge>

          <Button
            variant="outline"
            size="sm"
            className="pressable rounded-apple-sm"
            disabled={!frozen || campaign.resultStatus === 'pending_review'}
            onClick={() =>
              ask({
                title: '退回「终榜待审核」？',
                description:
                  '把结果状态改回待审核。前台不再把名次当作已定稿的最终结果展示，奖项高亮会撤下。',
                confirmLabel: '确认退回',
                successMessage: '结果状态已改为待审核',
                run: (reason) =>
                  api.post(`/supernova/campaigns/${id}/result-status`, {
                    resultStatus: 'pending_review',
                    reason,
                  }),
              })
            }
          >
            退回待审核
          </Button>

          <Button
            size="sm"
            className="pressable rounded-apple-sm"
            disabled={!frozen || campaign.resultStatus === 'final'}
            onClick={() =>
              ask({
                title: '确认置为最终结果？',
                description:
                  '前台会把这份名单当作已定稿的最终结果展示，含递补名次与预计奖励。请先在下方榜单预览里逐榜核对过 awardRank 与不合格原因。',
                confirmLabel: '确认定稿',
                destructive: true,
                successMessage: '结果状态已改为最终',
                run: (reason) =>
                  api.post(`/supernova/campaigns/${id}/result-status`, {
                    resultStatus: 'final',
                    reason,
                  }),
              })
            }
          >
            置为最终结果
          </Button>

          {!frozen && <span className="text-[11px] text-mut2">冻结终榜之后才能切换结果状态。</span>}
        </div>
      </div>
    </section>
  )
}

// ── 报名列表 ────────────────────────────────────────────────────────────

function AddBatchPanel({ id, ask }: { id: string; ask: (action: PendingAction) => void }) {
  const [paste, setPaste] = useState('')
  const [result, setResult] = useState<AddBatchResult | null>(null)
  const parsed = parseAddressPaste(paste)

  const submit = () => {
    setResult(null)
    ask({
      title: '批量添加报名？',
      description: (
        <>
          新增地址从本次添加时的区块开始计入活动；已经报名或取消资格的地址会跳过。
          钱包报名影响该钱包的个人榜资格；非 Agent 代币可在代币资格中独立人工放行。
          <span className="mt-2 block text-[11.5px] text-txt">
            本次 {parsed.addresses.length} 个地址
            {parsed.duplicates > 0 ? `（已去掉 ${parsed.duplicates} 个重复）` : ''}。
          </span>
        </>
      ),
      confirmLabel: '确认添加',
      successMessage: '批量添加完成',
      describe: addBatchSummary,
      invalidate: ['supernova-overview', 'supernova-registrations'],
      run: async (reason) => {
        const response = await api.post(`/supernova/campaigns/${id}/registrations/add-batch`, {
          addresses: parsed.addresses,
          reason,
        })
        setResult(readAddBatchResult(response))
        setPaste('')
        return response
      },
    })
  }

  return (
    <div className="surface space-y-3 p-6">
      <div className="space-y-1.5">
        <label htmlFor="supernova-add-paste" className="text-[12.5px] font-medium">
          批量添加报名
        </label>
        <p className="text-[11.5px] leading-relaxed text-mut2">
          粘贴钱包地址，由管理员加入本期报名。新增地址从添加时起计入统计；已有报名和黑名单不会被覆盖。
        </p>
        <Textarea
          id="supernova-add-paste"
          value={paste}
          placeholder="每行一个钱包地址，也可以用逗号、空格分隔，或直接粘贴 JSON 数组"
          className="min-h-28 resize-y rounded-apple font-mono text-[12px]"
          onChange={(event) => setPaste(event.target.value)}
        />
      </div>
      <div className="flex items-center justify-end">
        <Button
          className="pressable rounded-apple"
          disabled={parsed.addresses.length === 0 || parsed.addresses.length > 2_000}
          onClick={submit}
        >
          添加 {parsed.addresses.length} 个地址的报名
        </Button>
      </div>
      {paste.trim() !== '' && (
        <p className="text-[11.5px] text-mut2">
          识别到 {parsed.addresses.length} 个有效地址，去重 {parsed.duplicates} 个，
          无法识别 {parsed.invalidCount} 个{parsed.addresses.length > 2_000 ? '；一次最多 2000 个' : ''}。
        </p>
      )}
      {result && (
        <div className="space-y-1 rounded-apple border border-line/60 p-3 text-[11.5px] text-mut">
          <p>{addBatchSummary(result)}</p>
          <p>初始持仓已捕获 {result.openingCaptured} 个；待下一轮补抓 {result.openingPending} 个。</p>
          {result.invalid.length > 0 && (
            <p className="break-all font-mono text-rose">无效地址：{result.invalid.join('  ')}</p>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * 批量提交后的四类去向。**四类都摆出来，包括 0**——只显示非零的话，
 * 一次「全都是原本已取消」的提交会只剩一行字，而运营要确认的正是这几类之间的对照。
 */
function BatchOutcome({ result }: { result: DisqualifyBatchResult }) {
  return (
    <div className="space-y-2 rounded-apple border border-line/60 p-3">
      <p className="text-[11.5px] text-mut">
        本次提交 <span className="tnum font-mono text-txt">{result.total}</span> 个地址
      </p>
      <div className="flex flex-wrap gap-x-5 gap-y-1.5">
        {BATCH_BUCKETS.map((bucket) => (
          <div key={bucket} className="text-[11.5px]">
            <span className="text-mut2">{BATCH_BUCKET_LABELS[bucket]}</span>{' '}
            <span
              className={cn(
                'tnum font-mono',
                bucket === 'invalid' && result.invalid.length > 0 ? 'text-rose' : 'text-txt',
              )}
            >
              {result[bucket].length}
            </span>
          </div>
        ))}
      </div>
      {/*
        invalid **逐条列出**。只报一个数字的话，运营手上那份几百行的清单里
        究竟是哪一行抄错了无从查起——而这正是他们下一步唯一要做的事。
      */}
      {result.invalid.length > 0 && (
        <div className="space-y-1">
          <p className="text-[10.5px] text-mut2">以下片段不是合法地址，本次未处理：</p>
          <p className="break-all font-mono text-[10.5px] text-rose">{result.invalid.join('  ')}</p>
        </div>
      )}
    </div>
  )
}

/**
 * 批量取消资格 / 黑名单导入。
 *
 * 与逐行「取消资格」的差别不只是快：清单里**没报过名**的地址会被写成一行黑名单占位
 * 记录，之后它来报名会被直接拒绝。所以这个入口能影响的地址集合比报名表大。
 */
function DisqualifyBatchPanel({ id, ask }: { id: string; ask: (action: PendingAction) => void }) {
  const [paste, setPaste] = useState('')
  const [result, setResult] = useState<DisqualifyBatchResult | null>(null)
  const parsed = parseAddressPaste(paste)

  const submit = () =>
    ask({
      title: '批量取消资格？',
      description: (
        <>
          已报名的地址会被取消资格，从三个榜单中移除；没报名的地址会被写入黑名单，
          之后它来报名会被直接拒绝。已经冻结的终榜不会回溯。
          <span className="mt-2 block text-[11.5px] text-txt">
            本次 {parsed.addresses.length} 个地址
            {parsed.duplicates > 0 ? `（已去掉 ${parsed.duplicates} 个重复）` : ''}。
          </span>
        </>
      ),
      confirmLabel: '确认提交',
      destructive: true,
      successMessage: '批量处理完成',
      describe: batchSummary,
      invalidate: ['supernova-overview', 'supernova-registrations'],
      run: async (reason) => {
        const response = await api.post(
          `/supernova/campaigns/${id}/registrations/disqualify-batch`,
          { addresses: parsed.addresses, reason },
        )
        setResult(readBatchResult(response))
        setPaste('')
        return response
      },
    })

  return (
    <div className="surface space-y-3 p-6">
      <div className="space-y-1.5">
        <label htmlFor="supernova-disqualify-paste" className="text-[12.5px] font-medium">
          批量取消资格
        </label>
        <p className="text-[11.5px] leading-relaxed text-mut2">
          没报名的地址也可以加入黑名单，之后该地址报名会被拒绝；已报名的地址取消资格后从三个榜单移除。
        </p>
        <Textarea
          id="supernova-disqualify-paste"
          value={paste}
          placeholder="每行一个地址，也可以用逗号、空格分隔，或直接粘贴一段 JSON 数组"
          className="min-h-28 resize-y rounded-apple font-mono text-[12px]"
          onChange={(event) => setPaste(event.target.value)}
        />
      </div>

      <div className="flex flex-wrap items-center justify-end gap-3">
        <Button
          variant="destructive"
          className="pressable rounded-apple"
          disabled={parsed.addresses.length === 0}
          onClick={submit}
        >
          取消 {parsed.addresses.length} 个地址的资格
        </Button>
      </div>

      {paste.trim() !== '' && (
        <div className="space-y-1.5 rounded-apple border border-line/60 p-3 text-[11.5px]">
          <p className="text-mut">
            识别到 <span className="tnum font-mono text-txt">{parsed.addresses.length}</span>{' '}
            个有效地址
            {parsed.duplicates > 0 && <>，去掉 {parsed.duplicates} 个重复</>}
            {parsed.invalidCount > 0 && (
              <>
                ，<span className="text-rose">{parsed.invalidCount} 个无法识别</span>
              </>
            )}
            。
          </p>
          {parsed.invalid.length > 0 && (
            <p className="break-all font-mono text-[10.5px] text-rose">
              {parsed.invalid.join('  ')}
              {parsed.invalidCount > parsed.invalid.length && ' …'}
            </p>
          )}
        </div>
      )}

      {result && <BatchOutcome result={result} />}
    </div>
  )
}

/**
 * 一次导入后的三类去向。**三类都摆出来，包括 0** —— 理由与 `BatchOutcome` 同一条：
 * 一次「源期的人这一期全都报过名了」的导入，与一次「全都是新增」在只显示非零时长得一样。
 *
 * 地址每类最多逐条列 `IMPORT_LIST_LIMIT` 个：一期几百个地址整份铺开会把面板撑成
 * 一屏滚不完的十六进制，而在这里要确认的是数字对不对，逐个核地址是下面列表的事。
 */
function ImportOutcome({ result }: { result: ImportRegistrationsResult }) {
  return (
    <div className="space-y-2 rounded-apple border border-line/60 p-3">
      <p className="text-[11.5px] text-mut">
        源期 <span className="tnum font-mono text-txt">{result.sourceTotal}</span> 个已报名地址
        {result.sourceCampaignId !== '' && `（${result.sourceCampaignId}）`}
      </p>
      <div className="flex flex-wrap gap-x-5 gap-y-1.5">
        {IMPORT_BUCKETS.map((bucket) => (
          <div key={bucket} className="text-[11.5px]">
            <span className="text-mut2">{IMPORT_BUCKET_LABELS[bucket]}</span>{' '}
            <span className="tnum font-mono text-txt">{result[bucket].length}</span>
          </div>
        ))}
      </div>

      {/*
        初始持仓不属于上面那份账：抄不到**不影响参赛资格**，只影响盈亏榜的成本基准，
        漏掉的由快照任务补抓。混进三类计数里会让人以为有几个地址没导进去。
      */}
      <p className="text-[10.5px] text-mut2">
        已抄到初始持仓 <span className="tnum font-mono text-mut">{result.openingCaptured}</span> 个
        {result.openingPending > 0 && (
          <>
            ，还差 <span className="tnum font-mono text-warn">{result.openingPending}</span>{' '}
            个，由下一轮快照补抓
          </>
        )}
        。
      </p>

      {IMPORT_BUCKETS.filter((bucket) => result[bucket].length > 0).map((bucket) => (
        <div key={bucket} className="space-y-1">
          <p className="text-[10.5px] text-mut2">{IMPORT_BUCKET_LABELS[bucket]}：</p>
          <p className="break-all font-mono text-[10.5px] text-mut">
            {result[bucket].slice(0, IMPORT_LIST_LIMIT).join('  ')}
          </p>
          {result[bucket].length > IMPORT_LIST_LIMIT && (
            <p className="text-[10.5px] text-dim">
              …另有 {result[bucket].length - IMPORT_LIST_LIMIT} 个未列出
            </p>
          )}
        </div>
      ))}
    </div>
  )
}

/**
 * 从往期导入报名。
 *
 * 把源期状态为**已报名**的地址，在本期各落一行已报名。本期已有的地址一律跳过 ——
 * 尤其是**已取消资格的不会被翻回来**：那会让一次批量导入悄悄撤销掉一次人工裁决。
 *
 * 导入行没有本期的签名（nonce 记的是源期），但它是真实报名：能进榜，被取消资格后
 * 「恢复」照样是翻回已报名，而不是像黑名单占位那样删行。
 */
function ImportRegistrationsPanel({
  id,
  campaignName,
  ask,
}: {
  id: string
  campaignName: string
  ask: (action: PendingAction) => void
}) {
  const [source, setSource] = useState('')
  const [reason, setReason] = useState('')
  const [result, setResult] = useState<ImportRegistrationsResult | null>(null)

  /**
   * 与活动列表页共用同一条查询（同一个 key）。它已经带了每期的阶段与报名计数，
   * 所以这里不需要为了填一个下拉框再定义一条接口。
   */
  const campaigns = useQuery({
    queryKey: ['supernova-campaigns'],
    queryFn: () => api.get<{ data: SupernovaCampaignRow[] }>('/supernova/campaigns'),
    retry: false,
  })

  /** 本期不在选项里：源期 = 目标期在后端是 400，摆出来只会让人点一次才知道点不了。 */
  const options = (campaigns.data?.data ?? []).filter((row) => row.id !== id)
  const selected = options.find((row) => row.id === source) ?? null
  const empty = campaigns.isSuccess && options.length === 0

  const submit = () => {
    if (!selected) return
    // 先把上一次的回执撤掉：这一次失败的话，留在下面的仍是上一次的三类计数，
    // 而它看起来就像刚才那一次成功了。
    setResult(null)
    ask({
      title: '从往期导入报名？',
      description: (
        <>
          把「{selected.name}」里状态为已报名的地址，在「{campaignName}」各落一行已报名。
          本期已报名、已取消资格与黑名单的地址会被跳过，不覆写、不翻状态。
          <span className="mt-2 block text-[11.5px] text-txt">
            源期 {selected.name}（{selected.id}），当前 {selected.registrations.registered}{' '}
            个已报名地址。
          </span>
        </>
      ),
      confirmLabel: '确认导入',
      successMessage: '导入完成',
      describe: importSummary,
      // 面板里已经填过一次理由，带进弹窗免得同一句话打两遍；弹窗里照样能改。
      initialReason: reason,
      invalidate: ['supernova-overview', 'supernova-registrations'],
      run: async (submitted) => {
        const response = await api.post(`/supernova/campaigns/${id}/registrations/import`, {
          sourceCampaignId: selected.id,
          reason: submitted,
        })
        setResult(readImportResult(response))
        return response
      },
    })
  }

  return (
    <div className="surface space-y-3 p-6">
      <div className="space-y-1.5">
        <p className="text-[12.5px] font-medium">从往期导入报名</p>
        <ul className="space-y-0.5 text-[11.5px] leading-relaxed text-mut2">
          <li>只导入该期状态为「已报名」的地址。</li>
          <li>本期已报名、已取消资格与黑名单的地址会被跳过，不覆写、不翻状态。</li>
          <li>导入的地址从导入时刻起计入统计；活动还没开始的话从活动开始起算。</li>
          <li>导入行没有本期签名，该钱包之后自己报名会自动补上签名，报名时间不变。</li>
        </ul>
      </div>

      {/* 列表读不到时**只让这个面板降级**：报名列表本身与它无关，照常可用。 */}
      {campaigns.isError && (
        <div className="flex flex-wrap items-center gap-3 rounded-apple bg-warn/[0.07] px-3.5 py-2.5 text-[11.5px] text-warn">
          <span className="min-w-0 flex-1">读不到活动列表：{errorMessage(campaigns.error)}</span>
          <Button
            variant="outline"
            size="sm"
            className="pressable rounded-apple-sm"
            onClick={() => void campaigns.refetch()}
          >
            重试
          </Button>
        </div>
      )}

      {empty && (
        <p className="text-[11.5px] text-mut2">还没有别的活动期可导——这是目前唯一的一期。</p>
      )}

      <div className="grid gap-3 sm:grid-cols-[minmax(0,20rem)_1fr]">
        <div className="space-y-1.5">
          <label htmlFor="supernova-import-source" className="text-[12.5px] font-medium">
            来源活动期
          </label>
          <Select value={source} onValueChange={setSource} disabled={options.length === 0}>
            <SelectTrigger
              id="supernova-import-source"
              className="h-9 w-full rounded-apple text-[13px]"
            >
              <SelectValue placeholder={campaigns.isPending ? '读取中…' : '选择一期活动'} />
            </SelectTrigger>
            <SelectContent className="rounded-apple">
              {options.map((row) => (
                <SelectItem key={row.id} value={row.id}>
                  {row.name} · {row.id} · {PHASE_META[row.phase]?.label ?? row.phase}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/*
          理由框**不跟着活动列表的状态禁用**：列表还在读、或者读挂了，都不妨碍先把
          理由写好。禁用留给真正依赖那份列表的两件东西——下拉与提交按钮。
        */}
        <ReasonField id="supernova-import-reason" value={reason} onChange={setReason} />
      </div>

      <div className="flex flex-wrap items-center justify-end gap-3">
        <Button
          className="pressable rounded-apple"
          disabled={selected === null || !reasonValid(reason)}
          onClick={submit}
        >
          从该期导入报名
        </Button>
      </div>

      {result && <ImportOutcome result={result} />}
    </div>
  )
}

/**
 * 一次同步回执里的计数念成一句人话。**每一类都念，包括 0** ——
 * 运营点完这个按钮第一个要确认的就是「这次到底同步进来几个」，
 * 而「0 个」恰恰是最需要被念出来的那个答案（它说明名单没变，不是按钮没生效）。
 */
const PARTNER_COUNT_LABELS = [
  ['fetched', '拉到'],
  ['imported', '新增'],
  ['alreadyRegistered', '已存在'],
  /**
   * 「本来就在名单里、这一轮只补写了对账标识」。**不并进「新增」** ——
   * 那些地址早就报过名（自己签的、或往期导入的），并进去会让「合作方带来多少人」
   * 这笔账凭空变大，而那正是这块面板要回答的问题。
   */
  ['partnerRefBackfilled', '补写对账标识'],
  ['skippedDisqualified', '已取消资格跳过'],
  ['invalid', '无法识别'],
] as const

/**
 * 同步失败时那三句话。**按错误码给，说的是「下一步该做什么」**（契约 §3）。
 *
 * 服务端那句话只说了"发生了什么"，而这三种失败对应三个完全不同的动作：
 * 去把对方期填上 / 等上一轮跑完 / 这一期已经结了别再试。压成一句「同步失败」
 * 等于把运营留在原地。认不出的码回 null，照旧回显服务端原话。
 */
function partnerSyncError(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null

  switch (error.errorCode) {
    case 'SN_PARTNER_SYNC_RUNNING':
      return '正在同步：上一轮还没跑完，等它结束后再试（跨副本互斥，5 分钟无进展会自动释放）。'
    case 'SN_PARTNER_NOT_CONFIGURED':
      return '还没配置对方活动：先在上面填「对方活动标识」并保存，再点同步。'
    case 'SN_CAMPAIGN_CLOSED':
      return '这一期已冻结或已结束，不再接收新的报名同步。'
    default:
      return null
  }
}

/**
 * 「几个地址」这个量的两种形状。
 *
 * `POST run` 的回执给的是**地址数组**，而落库的 `lastResult`（`GET partner-sync` 那一份）
 * 是**压好的计数**。同一个面板要念这两份，所以在这里归一化成数字 ——
 * 只认一种的话，另一种会被静默跳过，面板上就只剩一句「还没有跑过」。
 */
function partnerCount(value: unknown): number | null {
  if (typeof value === 'number') return value
  return Array.isArray(value) ? value.length : null
}

function partnerSyncSummary(result: unknown): string | null {
  if (!result || typeof result !== 'object') return null
  const counts = result as Record<string, unknown>

  /**
   * 「压根没跑」单独念一句（后端定稿的 `skipped: "not_configured"`）。
   *
   * 它与「跑了但一个都没同步进来」在计数上长得一模一样（全 0），而前者说明这一步
   * 白点了 —— 要去把对方期填上，后者只是名单没变。
   */
  if (typeof counts.skipped === 'string') {
    return counts.skipped === 'not_configured'
      ? '未配置对方活动，这一轮没有同步'
      : `未同步（${counts.skipped}）`
  }

  const parts = PARTNER_COUNT_LABELS.flatMap(([key, label]) => {
    const count = partnerCount(counts[key])
    return count === null ? [] : [`${label} ${count}`]
  })

  return parts.length > 0 ? parts.join('，') : null
}

/**
 * 合作方（Metagents）报名同步（契约 phase 14 §3）。
 *
 * 一块面板办三件事：**配置**（开关 / 对方期 slug / 间隔）、**状态**（最近运行、最近成功、
 * 连续失败、上次计数）、**手动跑一轮**。
 *
 * 配置走的是活动配置那条 PUT，但**只发这三个键**（patch 语义）—— 与「活动配置」整张表
 * 分开提交是有意的：那张表是整份提交的，把这三项并进去会让一次「只想开一下同步」
 * 的操作顺带把表单里所有字段重写一遍，而其中任何一项被别人并发改过就会被覆盖回去。
 *
 * 当前配置**读的是 `GET partner-sync`**，不是活动行：那三列要等镜像库重新 `db pull`
 * 才读得到，而这条接口现在就答得出（它本来就要报「配没配好」）。
 */
function PartnerSyncPanel({ id, ask }: { id: string; ask: (action: PendingAction) => void }) {
  const canManage = useCan()('supernova', 'manage')

  const query = useQuery({
    queryKey: ['supernova-partner-sync', id],
    queryFn: () => api.get<{ data: SupernovaPartnerSync }>(`/supernova/campaigns/${id}/partner-sync`),
    retry: false,
    refetchInterval: 60_000,
  })

  const status = query.data?.data ?? null

  /**
   * 表单值**跟着服务端那一份重挂载**（`key` 换成 updatedAt 那一套在这里不适用，
   * 因为这条查询没有 updatedAt），所以用一个显式的「已编辑」标记：没编辑过就显示
   * 服务端的值，编辑过就显示本地的 —— 否则每 60 秒一次的轮询会把正在输入的内容冲掉。
   */
  const [draft, setDraft] = useState<{
    enabled: boolean
    slug: string
    intervalMin: string
  } | null>(null)

  const form = draft ?? {
    enabled: status?.enabled ?? false,
    slug: status?.slug ?? '',
    intervalMin: String(status?.intervalMin ?? 2),
  }
  const patch = (next: Partial<typeof form>) => setDraft({ ...form, ...next })

  const slugValid = form.slug.trim() === '' || /^[A-Za-z0-9_-]{1,64}$/.test(form.slug.trim())
  const intervalValid = inRange(form.intervalMin, 1, 1_440)
  /** 开着同步却没填对方期 = 一个永远跑不起来的配置，所以这时 slug 必填。 */
  const configComplete = !form.enabled || form.slug.trim() !== ''
  const dirty = draft !== null

  const saveConfig = () =>
    ask({
      title: '保存合作方同步配置？',
      description: (
        <>
          {form.enabled
            ? '打开后调度会按下面的间隔自动拉对方名单，并把新地址在这一期落成已报名。'
            : '关闭后调度不再自动拉取；「立即同步」按钮照样可用。'}
          <span className="mt-2 block text-[11.5px] text-txt">
            对方期 {form.slug.trim() || '（未填）'} · 间隔 {form.intervalMin} 分钟
          </span>
        </>
      ),
      confirmLabel: '确认保存',
      successMessage: '合作方同步配置已保存',
      invalidate: ['supernova-overview', 'supernova-partner-sync'],
      run: async (reason) => {
        const response = await api.put(`/supernova/campaigns/${id}`, {
          partnerSyncEnabled: form.enabled,
          // 空串在这条接口上的含义是「清掉对方期」，而它只收 null 或合法 slug。
          partnerCampaignSlug: form.slug.trim() === '' ? null : form.slug.trim(),
          partnerSyncIntervalMin: Number(form.intervalMin),
          reason,
        })
        // 保存成功后回到「跟着服务端」，免得本地草稿把后续的轮询结果挡住。
        setDraft(null)
        return response
      },
    })

  const runNow = () =>
    ask({
      title: '立即跑一轮合作方同步？',
      description: (
        <>
          现在就去拉一次对方名单，把新地址在这一期落成已报名。<b>幂等</b>
          ：已经存在的地址会被跳过，不覆写、不翻状态。自动开关关着也能跑。
          <span className="mt-2 block text-[11.5px] text-txt">
            对方期 {status?.slug ?? '（未配置）'}
          </span>
        </>
      ),
      confirmLabel: '确认同步',
      successMessage: '同步完成',
      describe: (data) => {
        // 回执信封是 `{ ok, audited, data: { ok, result } }`（服务端那一层包了一次）
        const envelope = data as { data?: { result?: unknown } } | null
        return partnerSyncSummary(envelope?.data?.result ?? null)
      },
      describeError: partnerSyncError,
      invalidate: ['supernova-overview', 'supernova-registrations', 'supernova-partner-sync'],
      run: (reason) => api.post(`/supernova/campaigns/${id}/partner-sync/run`, { reason }),
    })

  if (!canManage) return null

  const running = Boolean(status?.runningSince)
  const lastCounts = partnerSyncSummary(status?.lastResult ?? null)
  /**
   * 上一轮的两个附加事实：**没拉完**、以及认不出来的地址长什么样。
   *
   * 形状按 `looseObject` 读回来，所以这里用窄断言取那两个键 —— 少一个键只影响
   * 这两行提示，不该让整块面板认不出来。
   */
  const lastResult = (status?.lastResult ?? null) as {
    truncated?: boolean | null
    invalidSample?: string[] | null
  } | null
  const truncated = lastResult?.truncated === true
  const invalidSample = lastResult?.invalidSample ?? []

  return (
    <div className="surface space-y-3 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1.5">
          <p className="text-[12.5px] font-medium">合作方报名同步（Metagents）</p>
          <ul className="space-y-0.5 text-[11.5px] leading-relaxed text-mut2">
            <li>对方名单里的地址会在这一期落成「已报名」，来源标为 Metagents。</li>
            <li>本期已有的地址（含已取消资格与黑名单）一律跳过，不覆写、不翻状态。</li>
            <li>幂等：同一个地址同步多少次都只有一行；已冻结的期不再接收。</li>
          </ul>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {running && (
            <Badge className="h-5 bg-warn/15 text-[10.5px] text-warn hover:bg-warn/15">
              正在同步
            </Badge>
          )}
          {/*
            **不跟着状态查询禁用。** 这个按钮不依赖那份状态：没配置时服务端回
            400 `SN_PARTNER_NOT_CONFIGURED`、正在跑时 409 `SN_PARTNER_SYNC_RUNNING`、
            已冻结时 409 `SN_CAMPAIGN_CLOSED`，三句话各自说清了下一步 ——
            而灰着的按钮只会让人以为权限不够。
          */}
          <Button
            variant="outline"
            size="sm"
            className="pressable rounded-apple-sm"
            onClick={runNow}
          >
            <RefreshCw className="size-3.5" />
            立即同步
          </Button>
        </div>
      </div>

      {/* 状态读不到时**只让这块面板降级**：报名列表与配置表单都与它无关。 */}
      {query.isError && (
        <div className="flex flex-wrap items-center gap-3 rounded-apple bg-warn/[0.07] px-3.5 py-2.5 text-[11.5px] text-warn">
          <span className="min-w-0 flex-1">读不到同步状态：{errorMessage(query.error)}</span>
          <Button
            variant="outline"
            size="sm"
            className="pressable rounded-apple-sm"
            onClick={() => void query.refetch()}
          >
            重试
          </Button>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex items-start justify-between gap-4 rounded-apple border border-line/60 p-3 sm:col-span-2">
          <div className="min-w-0">
            <div className="text-[12.5px] font-medium text-txt">自动同步</div>
            <p className="mt-0.5 text-[11px] leading-relaxed text-mut2">
              打开后调度按间隔自动拉取；关掉只停自动，手动同步不受影响。
            </p>
          </div>
          <Switch
            aria-label="自动同步"
            checked={form.enabled}
            onCheckedChange={(checked) => patch({ enabled: checked })}
          />
        </div>
        <TextField
          id="supernova-partner-slug"
          label="对方活动标识"
          value={form.slug}
          invalid={!slugValid || !configComplete}
          mono
          placeholder="例如 supernova-s1"
          hint="合作方那一侧的活动标识：字母、数字、下划线与连字符，1–64 位。留空 = 不同步。"
          onChange={(value) => patch({ slug: value })}
        />
        <TextField
          id="supernova-partner-interval"
          label="同步间隔（分钟）"
          value={form.intervalMin}
          invalid={!intervalValid}
          numeric
          min={1}
          hint="1–1440。活动未开始时按至少 10 分钟跑，进行中按这个值。"
          onChange={(value) => patch({ intervalMin: value })}
        />
      </div>

      {!configComplete && (
        <p className="text-[11.5px] text-rose">打开自动同步就必须填对方活动标识。</p>
      )}

      <div className="flex flex-wrap items-center justify-end gap-3">
        {dirty && (
          <Button
            variant="ghost"
            size="sm"
            className="pressable rounded-apple-sm"
            onClick={() => setDraft(null)}
          >
            撤销改动
          </Button>
        )}
        <Button
          className="pressable rounded-apple"
          disabled={!dirty || !slugValid || !intervalValid || !configComplete}
          onClick={saveConfig}
        >
          保存同步配置
        </Button>
      </div>

      {/*
        状态面板。**每一项都摆出来，包括「从未跑过」** —— 运营在这里要回答的是
        「同步到底在不在跑」，而一块只显示"有值的那几项"的面板答不出这个问题。
      */}
      <div className="grid gap-x-4 gap-y-3 border-t border-line/50 pt-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="配置状态">
          {status === null ? '—' : status.configured ? '已配置' : '未配置'}
        </Field>
        <Field label="最近运行">{formatDateTime(status?.lastRunAt ?? null)}</Field>
        <Field label="最近成功">{formatDateTime(status?.lastSuccessAt ?? null)}</Field>
        <Field label="连续失败">
          <span
            className={cn('tnum font-mono', (status?.failures ?? 0) > 0 ? 'text-rose' : 'text-txt')}
          >
            {status?.failures ?? 0}
          </span>
        </Field>
        <div className="sm:col-span-2 lg:col-span-4">
          <Field label="上次结果">{lastCounts ?? '还没有跑过'}</Field>
          {/*
            没拉完是**黄字而不是红字**：这一轮本身是成功的，只是撞上了分页页数或时长
            上限，下一轮接着拉。说不清这一点的话，运营看见「拉到 2000」会以为对方名单
            就这么多人。
          */}
          {truncated && (
            <p className="mt-1.5 text-[11.5px] text-warn">
              这一轮没拉完（页数/时长上限），下一轮继续。
            </p>
          )}
          {/*
            无效地址**逐条摆出来**（折叠着）。「对方名单里有 3 个无效地址」这句话没法
            行动，而看见那三条长什么样（少一位、夹了空格、根本不是地址）就知道该找
            对方要什么。
          */}
          {invalidSample.length > 0 && (
            <Collapsible className="mt-1.5">
              <CollapsibleTrigger className="inline-flex items-center gap-1 text-[11.5px] text-mut transition-colors hover:text-cyan">
                <ChevronDown className="size-3.5" />
                无效地址样本（{invalidSample.length}）
              </CollapsibleTrigger>
              <CollapsibleContent>
                <ul className="mt-1.5 space-y-0.5">
                  {invalidSample.map((item, index) => (
                    <li
                      key={`${item}-${index}`}
                      className="truncate font-mono text-[10.5px] text-mut2"
                      title={item}
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
        {status?.lastError && (
          <div className="sm:col-span-2 lg:col-span-4">
            <div className="flex items-start gap-2 rounded-apple bg-rose/[0.07] px-3.5 py-2.5 text-[11.5px] leading-relaxed text-rose">
              <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
              <span className="min-w-0 flex-1">最近一次失败：{status.lastError}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function RegistrationsSection({
  id,
  campaignName,
  bySource,
  ask,
}: {
  id: string
  campaignName: string
  /** 按来源的计数。来自概览那条查询（不新增请求）；读不到时整块不渲染。 */
  bySource: SupernovaOverview['registrations']['bySource'] | null
  ask: (action: PendingAction) => void
}) {
  const canManage = useCan()('supernova', 'manageRegistrations')
  const canManageActivity = useCan()('supernova', 'manage')
  const [status, setStatus] = useState('all')
  const [source, setSource] = useState('all')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const query = useQuery({
    queryKey: ['supernova-registrations', id, { status, source, search, page }],
    queryFn: () =>
      api.get<SupernovaRegistrations>(`/supernova/campaigns/${id}/registrations`, {
        q: search || undefined,
        status: status === 'all' ? undefined : status,
        source: source === 'all' ? undefined : source,
        page,
        limit: PER_PAGE,
      }),
    retry: false,
  })

  const rows = query.data?.data ?? []
  const columns = canManage ? 7 : 6

  const applySearch = () => {
    setSearch(searchInput.trim())
    setPage(1)
  }

  /**
   * 「恢复」对黑名单占位记录是**删掉这一行**，不是把状态翻成已报名——它没有签名，
   * 翻成已报名等于凭空造一条没验过签的报名。所以这一行的按钮文案与确认文案都要分叉，
   * 否则运营会以为点下去是"让它回到报名状态"。
   */
  const actionFor = (row: SupernovaRegistrationRow): PendingAction => {
    const disqualified = row.status === 'disqualified'
    const denylist = isDenylistRow(row.source)

    const restoreCopy = denylist
      ? '移出后该地址回到未报名状态——这一行是黑名单占位记录，它从来没有报过名，移出不会凭空给它一条报名。想参赛得由本人重新签名报名。'
      : '恢复后该钱包重新计入下一次快照；已经冻结的终榜不会回溯。'

    return {
      title: disqualified ? (denylist ? '移出黑名单？' : '恢复参赛资格？') : '取消参赛资格？',
      description: (
        <>
          {disqualified
            ? restoreCopy
            : '取消后该钱包从后续快照的三榜中剔除，本人仍可正常交易。已经冻结的终榜不会回溯。'}
          <span className="mt-2 block font-mono text-[11.5px] text-txt">
            {shortAddress(row.address)}
          </span>
        </>
      ),
      confirmLabel: '确认提交',
      destructive: !disqualified,
      successMessage: disqualified ? (denylist ? '已移出黑名单' : '已恢复资格') : '已取消资格',
      invalidate: ['supernova-overview', 'supernova-registrations'],
      run: (reason) =>
        api.post(
          `/supernova/campaigns/${id}/registrations/${row.address}/${
            disqualified ? 'reinstate' : 'disqualify'
          }`,
          { reason },
        ),
    }
  }

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2.5">
        <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
          <Users className="size-4 text-cyan" />
        </div>
        <div>
          <h2 className="text-[15px] font-medium tracking-tight text-txt">报名名单</h2>
          <p className="mt-0.5 text-[12px] text-mut2">
            报名即生效（链上签名验过就写库）。取消资格后该钱包从三榜中剔除，它发的币也会失去参赛资格。
          </p>
        </div>
      </div>

      {canManage && (
        <>
          <AddBatchPanel id={id} ask={ask} />
          <ImportRegistrationsPanel id={id} campaignName={campaignName} ask={ask} />
          <DisqualifyBatchPanel id={id} ask={ask} />
        </>
      )}
      {canManageActivity && <PartnerSyncPanel id={id} ask={ask} />}

      {/*
        按来源的计数。**四档都摆，包括 0** —— 开了合作方同步之后第一个要确认的就是
        「对方那边同步进来几个了」，而 0 恰恰是最需要被看见的那个答案。
      */}
      {bySource && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-apple border border-line/60 px-3.5 py-2.5 text-[11.5px] text-mut">
          {REGISTRATION_SOURCES.map((key) => (
            <span key={key}>
              {REGISTRATION_SOURCE_META[key]?.label ?? key}{' '}
              <span className="tnum font-mono text-txt">{bySource[key] ?? 0}</span>
            </span>
          ))}
        </div>
      )}

      <Toolbar trailing={<Count value={query.data?.total} unit="人" />}>
        <Select
          value={status}
          onValueChange={(value) => {
            setStatus(value)
            setPage(1)
          }}
        >
          <SelectTrigger className="h-9 w-36 rounded-apple text-[13px]" aria-label="报名状态">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-apple">
            <SelectItem value="all">全部状态</SelectItem>
            <SelectItem value="registered">已报名</SelectItem>
            <SelectItem value="disqualified">已取消资格</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={source}
          onValueChange={(value) => {
            setSource(value)
            setPage(1)
          }}
        >
          <SelectTrigger className="h-9 w-36 rounded-apple text-[13px]" aria-label="报名来源">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-apple">
            <SelectItem value="all">全部来源</SelectItem>
            {REGISTRATION_SOURCES.map((key) => (
              <SelectItem key={key} value={key}>
                {REGISTRATION_SOURCE_META[key]?.label ?? key}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          aria-label="搜索报名"
          value={searchInput}
          placeholder="按地址前缀搜索"
          className="h-9 w-full rounded-apple text-[13px] sm:w-72"
          onChange={(event) => setSearchInput(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && applySearch()}
          onBlur={applySearch}
        />
      </Toolbar>

      <TableCard>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-line/50 hover:bg-transparent">
                <TableHead className="section-label pl-6">地址</TableHead>
                <TableHead className="section-label">状态</TableHead>
                <TableHead className="section-label">来源</TableHead>
                <TableHead className="section-label">报名时间</TableHead>
                <TableHead className="section-label">初始持仓</TableHead>
                <TableHead className="section-label">规则版本</TableHead>
                {canManage && <TableHead className="section-label pr-6 text-right">操作</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.isPending &&
                Array.from({ length: 6 }, (_, index) => (
                  <TableRow key={index} className="border-line/40">
                    <TableCell colSpan={columns} className="px-6 py-2.5">
                      <Skeleton className="h-7 w-full rounded-apple-sm" />
                    </TableCell>
                  </TableRow>
                ))}

              {query.isError && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={columns}>
                    <EmptyState
                      icon={TriangleAlert}
                      title="读不到报名名单"
                      description={errorMessage(query.error)}
                    />
                  </TableCell>
                </TableRow>
              )}

              {query.isSuccess && rows.length === 0 && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={columns}>
                    <EmptyState
                      icon={Users}
                      title="没有符合条件的报名"
                      description="换一个状态筛选看看，或者清空搜索词。"
                    />
                  </TableCell>
                </TableRow>
              )}

              {rows.map((row) => {
                const statusMeta = STATUS_META[row.status]
                const sourceMeta = REGISTRATION_SOURCE_META[row.source]
                const disqualified = row.status === 'disqualified'
                const denylist = isDenylistRow(row.source)
                return (
                  <TableRow key={row.address} className="row-hover border-line/40">
                    <TableCell className="pl-6">
                      <RawId value={row.address} href={explorerAddressUrl(row.address)} />
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={cn(
                          'h-5 text-[10.5px]',
                          statusMeta?.className ?? 'bg-edge/10 text-mut hover:bg-edge/10',
                        )}
                      >
                        {statusMeta?.label ?? row.status}
                      </Badge>
                      {disqualified && row.disqualifiedReason && (
                        <div
                          className="mt-1 max-w-48 truncate text-[10.5px] text-mut2"
                          title={row.disqualifiedReason}
                        >
                          {row.disqualifiedReason}
                        </div>
                      )}
                    </TableCell>
                    {/*
                      黑名单占位记录没有签名，「恢复」对它是删行而不是翻状态。
                      不把来源标出来，两种行在列表里长得一模一样。

                      往期导入的行是**真实报名**（恢复照样是翻状态），单独一档；
                      源期挂在 title 上 —— 核对导入结果时第一个要问的就是「从哪一期来的」。
                    */}
                    <TableCell>
                      <Badge
                        className={cn(
                          'h-5 text-[10.5px]',
                          sourceMeta?.className ?? 'bg-edge/10 text-mut hover:bg-edge/10',
                        )}
                        title={
                          row.importedFrom
                            ? `来自 ${row.importedFrom}`
                            : row.partnerRef
                              ? `合作方标识 ${row.partnerRef}`
                              : undefined
                        }
                      >
                        {sourceMeta?.label ?? row.source}
                      </Badge>
                      {/*
                        合作方标识摆在徽章下面而不是只挂在 title 上：与对方名单逐条对账时
                        要能直接看见它，而 title 得把鼠标停上去才出来。
                      */}
                      {row.partnerRef && (
                        <div
                          className="mt-1 max-w-40 truncate font-mono text-[10.5px] text-mut2"
                          title={row.partnerRef}
                        >
                          {row.partnerRef}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-[11.5px] text-mut">
                      {formatDateTime(row.registeredAt)}
                      <div className="tnum font-mono text-[10.5px] text-dim">
                        {formatBlock(row.registeredBlock)}
                      </div>
                    </TableCell>
                    {/*
                      盈亏榜的初始批次来自这张表。没有行 = 这个钱包在盈亏榜上没有成本基准，
                      「这个人的盈亏怎么看着不对」第一个要看的就是这里。
                      0 条既可能是没抓到、也可能是报名时本来就空仓，两者分不开，所以只说「无」。
                    */}
                    <TableCell className="text-[11.5px] text-mut">
                      {row.opening.tokens > 0 ? (
                        <>
                          <span className="tnum font-mono">{row.opening.tokens}</span> 个代币
                          <div className="text-[10.5px] text-dim">
                            {formatDateTime(row.opening.capturedAt)}
                          </div>
                        </>
                      ) : (
                        <span className="text-dim" title="报名时空仓，或初始持仓尚未捕获">
                          无
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-[11.5px] text-mut">
                      {row.rulesVersion}
                    </TableCell>
                    {canManage && (
                      <TableCell className="pr-6 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          className="pressable rounded-apple-sm"
                          onClick={() => ask(actionFor(row))}
                        >
                          {disqualified ? (denylist ? '移出黑名单' : '恢复资格') : '取消资格'}
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      </TableCard>

      <Pager page={page} pageCount={query.data?.pageCount ?? 1} onChange={setPage} />
    </section>
  )
}

// ── 代币资格覆盖 ────────────────────────────────────────────────────────

function TokenCandidatesSection({
  id,
  frozen,
  canManage,
  ready,
  review,
}: {
  id: string
  frozen: boolean
  canManage: boolean
  ready: boolean
  review: (address: string, mode: 'allow' | 'deny') => void
}) {
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState('pending')
  const [search, setSearch] = useState('')
  const [scope, setScope] = useState<'registered' | 'all'>('registered')
  const query = useQuery({
    queryKey: ['supernova-token-candidates', id, { page, status, search, scope }],
    queryFn: () =>
      api.get<SupernovaTokenCandidates>(`/supernova/campaigns/${id}/token-candidates`, {
        page,
        limit: PER_PAGE,
        status,
        search,
        scope,
      }),
    retry: false,
  })
  return (
    <div className="surface space-y-4 p-6">
      <div>
        <h3 className="text-[14px] font-medium">非 Agent 代币审核</h3>
        <p className="mt-1 text-[12px] text-mut2">
          可按创建者报名情况或全部未绑定 Agent 代币查看；已下架代币不在列表中。
          人工通过按代币 CA 给予三榜资格，与创建者报名无关；创建者若已被取消资格，代币仍不具备资格。交易者仍须报名。下一次快照生效；采样市值仅供审核参考。
        </p>
      </div>
      <div className="flex flex-wrap gap-2" role="group" aria-label="代币审核范围">
        <Button
          size="sm"
          variant={scope === 'registered' ? 'default' : 'outline'}
          aria-pressed={scope === 'registered'}
          onClick={() => { setScope('registered'); setPage(1) }}
        >
          已报名创建者
        </Button>
        <Button
          size="sm"
          variant={scope === 'all' ? 'default' : 'outline'}
          aria-pressed={scope === 'all'}
          onClick={() => { setScope('all'); setPage(1) }}
        >
          全部非 Agent 代币
        </Button>
      </div>
      <div className="flex flex-wrap gap-3">
        <Input
          aria-label="搜索待审核代币"
          placeholder="名称、代号、CA 或创建者"
          value={search}
          className="max-w-sm"
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
        />
        <Select
          value={status}
          onValueChange={(value) => {
            setStatus(value)
            setPage(1)
          }}
        >
          <SelectTrigger className="w-36" aria-label="审核状态">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">待审核</SelectItem>
            <SelectItem value="allow">已通过</SelectItem>
            <SelectItem value="deny">已拒绝</SelectItem>
            <SelectItem value="all">全部</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>代币 / CA</TableHead>
              <TableHead>创建者 / 报名状态</TableHead>
              <TableHead className="text-right">采样市值（USD）</TableHead>
              <TableHead>审核状态</TableHead>
              {canManage && <TableHead>操作</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isPending && (
              <TableRow>
                <TableCell colSpan={canManage ? 5 : 4}>
                  <Skeleton className="h-12 w-full" />
                </TableCell>
              </TableRow>
            )}
            {query.isError && (
              <TableRow>
                <TableCell colSpan={canManage ? 5 : 4}>
                  <QueryError
                    title="读不到审核列表"
                    error={query.error}
                    onRetry={() => void query.refetch()}
                  />
                </TableCell>
              </TableRow>
            )}
            {query.isSuccess && query.data.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={canManage ? 5 : 4} className="py-8 text-center text-mut2">
                  没有符合条件的代币
                </TableCell>
              </TableRow>
            )}
            {query.data?.data.map((row) => (
              <TableRow key={row.tokenAddress}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {row.image ? (
                      <img src={row.image} alt="" className="size-9 rounded-full object-cover" />
                    ) : (
                      <span className="flex size-9 items-center justify-center rounded-full bg-edge/10">
                        {row.symbol.slice(0, 1)}
                      </span>
                    )}
                    <div>
                      <div>
                        {row.name} <span className="text-mut2">{row.symbol}</span>
                      </div>
                      <RawId value={row.tokenAddress} href={explorerTokenUrl(row.tokenAddress)} />
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <RawId value={row.creator} href={explorerAddressUrl(row.creator)} />
                  <div className="text-[11px] text-mut2">
                    {row.creatorStatus === 'disqualified'
                      ? '已取消资格'
                      : row.creatorStatus === 'registered' && row.registeredAt
                        ? formatDateTime(row.registeredAt)
                        : '未报名'}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="tnum font-mono">{formatUsd(row.mcapUsd)}</div>
                  <div className="text-[11px] text-mut2">
                    {row.sampledAt ? formatDateTime(row.sampledAt) : '暂无采样'}
                  </div>
                </TableCell>
                <TableCell>
                  <span title={row.reason ?? ''}>
                    {row.mode === 'allow' ? '已通过' : row.mode === 'deny' ? '已拒绝' : '待审核'}
                  </span>
                </TableCell>
                {canManage && (
                  <TableCell>
                    <div className="flex gap-2">
                      {row.mode !== 'allow' && (
                        <Button
                          size="sm"
                          disabled={!ready || frozen || row.creatorStatus === 'disqualified'}
                          title={row.creatorStatus === 'disqualified' ? '创建者已取消资格，本期不能通过' : undefined}
                          onClick={() => review(row.tokenAddress, 'allow')}
                        >
                          通过
                        </Button>
                      )}
                      {row.mode !== 'deny' && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!ready}
                          onClick={() => review(row.tokenAddress, 'deny')}
                        >
                          拒绝
                        </Button>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between">
        <Count value={query.data?.total ?? 0} unit="个代币" />
        <Pager page={page} pageCount={query.data?.pageCount ?? 1} onChange={setPage} />
      </div>
    </div>
  )
}

type OverrideList = { data: SupernovaTokenOverrideRow[] }

function TokenOverridesSection({
  id,
  ask,
  frozen,
}: {
  id: string
  ask: (action: PendingAction) => void
  frozen: boolean
}) {
  const canManage = useCan()('supernova', 'manageTokenEligibility')
  const [paste, setPaste] = useState('')
  const [mode, setMode] = useState<'allow' | 'deny'>('allow')

  const query = useQuery({
    queryKey: ['supernova-token-overrides', id],
    queryFn: () => api.get<OverrideList>(`/supernova/campaigns/${id}/token-overrides`),
    retry: false,
  })

  const rows = query.data?.data ?? []
  const parsed = parseAddressPaste(paste)
  const columns = canManage ? 6 : 4

  /**
   * ⚠️ **写入前这张表必须已经读出来了。**
   *
   * `PUT` 是整体替换：在列表还没加载完（或读失败）时提交，`rows` 是空数组，
   * 合并出来的就是"只有这次这几条"的一份清单，等于把表里其余覆盖全删掉——
   * 而界面只会显示「已更新」。所以读没成功之前，所有写入口一律禁用。
   */
  const ready = query.isSuccess && !query.isFetching

  const applied = (data: unknown) =>
    appliedSummary((data as { data?: { applied?: unknown } } | null)?.data?.applied)

  const invalidate = [
    'supernova-overview',
    'supernova-token-overrides',
    'supernova-token-candidates',
    'supernova-leaderboard',
  ]

  /**
   * 整表提交：当前这张表 + 这次的改动 - 这次要删掉的。
   *
   * `confirmClear` **由调用方显式传，绝不在这里按"结果为空就补上"推导**。
   * IGNIX 那道 409 SN_CONFIRM_CLEAR 防的正是「列表没读出来就提交」这类事故，
   * 自动补标记等于把守卫关掉 —— 事故路径会安安静静地拿到放行。
   */
  const replaceWith = async (
    reason: string,
    upsert: Parameters<typeof mergeOverrides>[1],
    remove: readonly string[] = [],
    options: { confirmClear?: boolean } = {},
  ) => {
    // Refresh before replacing the whole set so consecutive reviews preserve earlier approvals.
    const latest = await query.refetch({ throwOnError: true })
    if (!latest.data) throw new Error('读取现有覆盖失败，未提交审核')
    return api.put(`/supernova/campaigns/${id}/token-overrides`, {
      overrides: mergeOverrides(latest.data.data, upsert, remove),
      ...(options.confirmClear ? { confirmClear: true } : {}),
      reason,
    })
  }

  const submitBatch = () =>
    ask({
      title: mode === 'allow' ? '批量放行三榜资格？' : '批量取消代币资格？',
      description: (
        <>
          {mode === 'allow'
            ? '这些代币按 CA 取得三榜资格，不依赖 Agent 绑定或创建者报名；创建者若已被取消资格仍会排除。参与者报名后的有效交易计入交易量榜与盈利榜，交易与获奖门槛继续适用。下一次快照生效，不保证获奖。'
            : '这些代币会被直接取消参赛资格，无论它们是否已上架。'}
          <span className="mt-2 block text-[11.5px] text-txt">
            本次 {parsed.addresses.length} 个地址
            {parsed.duplicates > 0 ? `（已去掉 ${parsed.duplicates} 个重复）` : ''}， 表内原有{' '}
            {rows.length} 条会一并保留。
          </span>
        </>
      ),
      confirmLabel: '确认提交',
      destructive: mode === 'deny',
      successMessage: '代币资格覆盖已更新',
      describe: applied,
      invalidate,
      run: async (reason) => {
        const result = await replaceWith(
          reason,
          parsed.addresses.map((tokenAddress) => ({ tokenAddress, mode, reason })),
        )
        setPaste('')
        return result
      },
    })

  const flipRow = (row: SupernovaTokenOverrideRow) => {
    const next = row.mode === 'allow' ? 'deny' : 'allow'
    return ask({
      title: next === 'allow' ? '改为三榜人工放行？' : '改为取消资格？',
      description: (
        <>
          {next === 'allow'
            ? '此代币按 CA 取得三榜资格，不依赖 Agent 绑定或创建者报名；创建者若已被取消资格仍会排除。参与者报名后的有效交易计入交易量榜与盈利榜，交易与获奖门槛继续适用。下一次快照生效，不保证获奖。'
            : '这个代币会被取消三榜参赛资格；交易量与盈利统计在下一次快照重算，冻结终榜不会重新计算。'}
          <span className="mt-2 block font-mono text-[11.5px] text-txt">
            {row.symbol ? `${row.symbol} · ` : ''}
            {shortAddress(row.tokenAddress)}
          </span>
        </>
      ),
      confirmLabel: '确认修改',
      destructive: next === 'deny',
      successMessage: '代币资格覆盖已更新',
      describe: applied,
      invalidate,
      run: (reason) =>
        replaceWith(reason, [{ tokenAddress: row.tokenAddress, mode: next, reason }]),
    })
  }

  /**
   * 移除一条覆盖。**不是"改成另一种模式"** —— 是让这个代币回到自动判定
   * （按 Agent 上架状态与绑定关系算），所以整表提交时把它整行去掉。
   */
  const removeRow = (row: SupernovaTokenOverrideRow) => {
    // 这是界面上唯一会把整张表清空的操作，也是唯一该带 confirmClear 的地方。
    const clearsTable = rows.length === 1

    return ask({
      title: '移除这条覆盖？',
      description: (
        <>
          移除后这个代币的参赛资格回到自动判定（按 Agent 上架状态与 ASP 绑定关系），
          不再受人工覆盖影响。
          <span className="mt-2 block font-mono text-[11.5px] text-txt">
            {row.symbol ? `${row.symbol} · ` : ''}
            {shortAddress(row.tokenAddress)}
          </span>
          {/*
            这是整张表清空的**唯一一条正当路径**（写入是整表替换，空列表就是全删）。
            其余任何一次空列表提交都是事故，所以这一条要在确认前说清它的后果。
          */}
          {clearsTable && (
            <span className="mt-2 block text-[11.5px] text-warn">
              这是表里最后一条覆盖，移除后整张覆盖表将清空，所有代币都回到自动判定。
            </span>
          )}
        </>
      ),
      confirmLabel: '确认移除',
      destructive: true,
      successMessage: '覆盖已移除',
      describe: applied,
      invalidate,
      run: (reason) => replaceWith(reason, [], [row.tokenAddress], { confirmClear: clearsTable }),
    })
  }

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2.5">
        <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
          <FileText className="size-4 text-cyan" />
        </div>
        <div>
          <h2 className="text-[15px] font-medium tracking-tight text-txt">代币资格覆盖</h2>
          <p className="mt-0.5 text-[12px] text-mut2">
            「人工放行」的代币默认标记为 Tapeout，按 CA 取得三榜资格，与创建者报名无关；创建者已被取消资格的代币仍排除。
            参与者报名后的有效交易计入交易量榜与盈利榜，交易与获奖门槛继续适用；取消资格会排除该代币。
          </p>
        </div>
      </div>

      <TokenCandidatesSection
        id={id}
        frozen={frozen}
        canManage={canManage}
        ready={ready}
        review={(tokenAddress, mode) =>
          ask({
            title: mode === 'allow' ? '通过三榜资格审核？' : '拒绝代币参赛？',
            description:
              mode === 'allow'
                ? '此代币按 CA 取得三榜资格，不依赖 Agent 绑定或创建者报名；创建者若已被取消资格仍会排除。参与者报名后的有效交易计入交易量榜与盈利榜，交易与获奖门槛继续适用。下一次快照生效。'
                : '该代币将被取消资格。市值榜立即应用资格排除，统计在下次快照重算；冻结终榜不会重新计算交易量或盈利。此覆盖同时影响该代币在其他榜单中的统计。',
            confirmLabel: mode === 'allow' ? '确认通过' : '确认拒绝',
            destructive: mode === 'deny',
            successMessage: mode === 'allow' ? '审核已保存，下一次快照生效' : '代币资格已取消',
            describe: applied,
            invalidate,
            run: (reason) => replaceWith(reason, [{ tokenAddress, mode, reason }]),
          })
        }
      />
      {frozen && (
        <p className="text-[12px] text-warn">
          活动已冻结，审核通过不会新增终榜记录，因此已禁用新候选通过操作。仍可取消资格或管理已有覆盖。
        </p>
      )}

      {canManage && (
        <div className="surface space-y-3 p-6">
          <div className="space-y-1.5">
            <label htmlFor="supernova-override-paste" className="text-[12.5px] font-medium">
              粘贴代币地址
            </label>
            <Textarea
              id="supernova-override-paste"
              value={paste}
              placeholder="每行一个地址，也可以用逗号、空格分隔，或直接粘贴一段 JSON 数组"
              className="min-h-28 resize-y rounded-apple font-mono text-[12px]"
              onChange={(event) => setPaste(event.target.value)}
            />
          </div>

          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="space-y-1.5">
              <label htmlFor="supernova-override-mode" className="text-[12.5px] font-medium">
                写入方式
              </label>
              <Select value={mode} onValueChange={(value) => setMode(value as 'allow' | 'deny')}>
                <SelectTrigger
                  id="supernova-override-mode"
                  className="h-9 w-44 rounded-apple text-[13px]"
                  aria-label="写入方式"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-apple">
                  <SelectItem value="allow">人工放行</SelectItem>
                  <SelectItem value="deny">取消资格</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              className="pressable rounded-apple"
              disabled={!ready || parsed.addresses.length === 0}
              onClick={submitBatch}
            >
              提交 {parsed.addresses.length} 个地址
            </Button>
          </div>

          {!ready && (
            <p className="text-[11.5px] text-warn">
              覆盖表正在读取或读取失败，暂不能写入。写入是整表替换，现有覆盖必须完整读取后才能保留。
            </p>
          )}

          {paste.trim() !== '' && (
            <div className="space-y-1.5 rounded-apple border border-line/60 p-3 text-[11.5px]">
              <p className="text-mut">
                识别到 <span className="tnum font-mono text-txt">{parsed.addresses.length}</span>{' '}
                个有效地址
                {parsed.duplicates > 0 && <>，去掉 {parsed.duplicates} 个重复</>}
                {parsed.invalidCount > 0 && (
                  <>
                    ，<span className="text-rose">{parsed.invalidCount} 个无法识别</span>
                  </>
                )}
                。
              </p>
              {parsed.invalid.length > 0 && (
                <p className="break-all font-mono text-[10.5px] text-rose">
                  {parsed.invalid.join('  ')}
                  {parsed.invalidCount > parsed.invalid.length && ' …'}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <TableCard>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-line/50 hover:bg-transparent">
                <TableHead className="section-label pl-6">代币</TableHead>
                <TableHead className="section-label">方式</TableHead>
                <TableHead className="section-label">理由</TableHead>
                <TableHead className="section-label">最近修改</TableHead>
                {canManage && (
                  <>
                    <TableHead className="section-label text-right">改为</TableHead>
                    <TableHead className="section-label pr-6 text-right">移除</TableHead>
                  </>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.isPending &&
                Array.from({ length: 3 }, (_, index) => (
                  <TableRow key={index} className="border-line/40">
                    <TableCell colSpan={columns} className="px-6 py-2.5">
                      <Skeleton className="h-7 w-full rounded-apple-sm" />
                    </TableCell>
                  </TableRow>
                ))}

              {query.isError && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={columns}>
                    <EmptyState
                      icon={TriangleAlert}
                      title="读不到代币资格覆盖"
                      description={errorMessage(query.error)}
                    />
                  </TableCell>
                </TableRow>
              )}

              {query.isSuccess && rows.length === 0 && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={columns}>
                    <EmptyState
                      icon={FileText}
                      title="还没有任何覆盖"
                      description="没有覆盖时，市值榜按有效 Agent 绑定与创建者报名状态自动判定。"
                    />
                  </TableCell>
                </TableRow>
              )}

              {rows.map((row) => {
                const modeMeta = OVERRIDE_MODE_META[row.mode]
                return (
                  <TableRow key={row.tokenAddress} className="row-hover border-line/40">
                    <TableCell className="pl-6">
                      <div className="text-[12.5px] text-txt">
                        {row.symbol ?? row.name ?? '未知代币'}
                      </div>
                      <RawId value={row.tokenAddress} href={explorerTokenUrl(row.tokenAddress)} />
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={cn(
                          'h-5 text-[10.5px]',
                          modeMeta?.className ?? 'bg-edge/10 text-mut hover:bg-edge/10',
                        )}
                      >
                        {modeMeta?.label ?? row.mode}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-56">
                      <div className="truncate text-[11.5px] text-mut" title={row.reason ?? ''}>
                        {row.reason || '—'}
                      </div>
                    </TableCell>
                    <TableCell className="text-[11.5px] text-mut">
                      {formatDateTime(row.updatedAt)}
                      <div className="text-[10.5px] text-dim">{row.updatedBy ?? '未知账号'}</div>
                    </TableCell>
                    {canManage && (
                      <>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            className="pressable rounded-apple-sm"
                            disabled={!ready}
                            onClick={() => flipRow(row)}
                          >
                            {row.mode === 'allow' ? '改为取消资格' : '改为人工放行'}
                          </Button>
                        </TableCell>
                        <TableCell className="pr-6 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="pressable rounded-apple-sm text-mut"
                            disabled={!ready}
                            onClick={() => removeRow(row)}
                          >
                            移除
                          </Button>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      </TableCard>
    </section>
  )
}

// ── 榜单预览 ────────────────────────────────────────────────────────────

/**
 * 每张榜的列数。骨架屏、读取失败与空态那三行的 `colSpan` 都读它。
 *
 * 算出来而不是各写一个字面量：规则 v1.2 给市值榜加了「流动性」、给交易量榜加了
 * 「原始成交额」之后，三张榜的列数第一次**两两不同**。写死的话下一次加列时，
 * 那三行会在某一张榜上错位一格，而正常有数据的路径上完全看不出来。
 */
function columnCount(board: Board): number {
  // 名次 / 获奖名次 / 代币或钱包 / 计分值 / 获奖资格 / 预计奖励
  const base = 6
  if (board === 'mcap') return base + 2 // 实时市值 + 流动性
  if (board === 'volume') return base + 2 // 原始成交额 + 低于门槛
  return base + 1 // 盈亏明细
}

type Diagnostics = SupernovaLeaderboardRow['diagnostics']
type QuoteAmount = Extract<
  NonNullable<Diagnostics>,
  { kind: 'pnl' }
>['excludedExternalProceeds'][number]

/**
 * 「低于门槛」。
 *
 * 交易量为 0 时运营的第一个问题是「他真没交易，还是每一笔都太小」，而计分额那一列
 * 对这两种情况给出的是同一个 0。这一格是**唯一**能当场分开这两种情况的地方 ——
 * 否则只能去翻快照明细的 Json。
 *
 * 明细来自镜像库，读不到时是 null：那时这一格显示 —，而不是一个看起来像真数据的 0。
 */
function SkippedCell({ diagnostics }: { diagnostics: Diagnostics }) {
  if (!diagnostics || diagnostics.kind !== 'volume') {
    return <span className="text-dim">—</span>
  }

  const notes = [
    diagnostics.skippedIneligible ? `${diagnostics.skippedIneligible} 笔不合格代币` : null,
    diagnostics.missingFx ? `${diagnostics.missingFx} 笔无汇率` : null,
  ].filter((note): note is string => note !== null)

  return (
    <div className="flex flex-col items-end gap-0.5">
      <span
        className={cn(
          'tnum font-mono text-[12px]',
          diagnostics.skippedBelowMin ? 'text-txt' : 'text-dim',
        )}
      >
        {diagnostics.skippedBelowMin ? diagnostics.skippedBelowMin : '—'}
      </span>
      {notes.length > 0 && <span className="text-[10px] text-mut2">另有 {notes.join(' / ')}</span>}
    </div>
  )
}

/**
 * 一笔计价币金额。
 *
 * 精度查不到时**不显示破折号**：盈亏明细是拿来核对的，一个「—」等于把唯一的线索也
 * 抹掉。照 `isUnknownQuote` 的既有做法，原样给出最小单位并标注，完整地址挂在 title 上。
 */
function QuoteText({ value, tone }: { value: QuoteAmount; tone?: string }) {
  const unknown = isUnknownQuote(value)

  return (
    <span className="tnum font-mono whitespace-nowrap">
      <span className={tone ?? 'text-txt'}>
        {unknown ? value.amount : formatUnits(value.amount, value.quoteDecimals, 6)}
      </span>{' '}
      <span
        className={unknown ? 'text-warn' : 'text-mut2'}
        title={
          unknown ? `${value.quote}（ignix 运行时参数里没有这个资产，精度未知）` : value.quote
        }
      >
        {unknown ? '最小单位' : quoteLabel(value)}
      </span>
    </span>
  )
}

/** 负数标红。0 与精度未知都保持中性——那两种情况谈不上方向。 */
function pnlTone(value: QuoteAmount): string {
  if (isUnknownQuote(value) || !/^-?\d+$/.test(value.amount)) return 'text-mut2'
  if (BigInt(value.amount) === 0n) return 'text-mut2'
  return value.amount.startsWith('-') ? 'text-dn' : 'text-txt'
}

/**
 * 盈亏明细。
 *
 * 「盈亏 0」的答案几乎总在初始批次里：报名后没有价格样本的那一批**没有成本基准**，
 * 它卖出去的所得永远不进分子。公共榜单接口不给这些字段（也不该给），所以这一块是从
 * 后台自己的镜像库补的；补不到时整个展开入口不出现，而不是展开一个空盒子。
 */
function PnlDetailCell({ diagnostics }: { diagnostics: Diagnostics }) {
  const [open, setOpen] = useState(false)

  if (!diagnostics || diagnostics.kind !== 'pnl') {
    return <span className="text-dim">—</span>
  }

  const { realizedByToken, openingByToken, excludedExternalProceeds } = diagnostics
  const empty =
    realizedByToken.length === 0 &&
    openingByToken.length === 0 &&
    excludedExternalProceeds.length === 0

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="pressable h-7 rounded-apple-sm px-2 text-[11.5px] text-mut"
        >
          明细
          <ChevronDown className={cn('size-3.5 transition-transform', open && 'rotate-180')} />
        </Button>
      </CollapsibleTrigger>

      <CollapsibleContent className="mt-1.5 space-y-2.5 rounded-apple border border-line/60 px-3 py-2.5 text-[11.5px]">
        {empty && <p className="text-mut2">没有已实现的买卖配对</p>}

        {openingByToken.length > 0 && (
          <div>
            <div className="section-label mb-1">初始批次</div>
            <ul className="space-y-0.5">
              {openingByToken.map((lot) => (
                <li key={lot.token} className="flex flex-wrap items-baseline gap-x-1.5">
                  <span className="text-txt" title={lot.token}>
                    {lot.tokenSymbol ?? shortAddress(lot.token)}
                  </span>
                  <span className="tnum font-mono text-mut">
                    数量 {formatUnits(lot.tokenAmount, LAUNCH_TOKEN_DECIMALS, 4)}
                  </span>
                  <span className="text-mut2">·</span>
                  <span className="text-mut">
                    成本 <QuoteText value={lot.cost} tone="text-mut" />
                  </span>
                  <span className="text-mut2">·</span>
                  <span className={lot.external ? 'text-warn' : 'text-mut2'}>
                    {lot.external
                      ? '无成本基准（报名后无价格样本，这批卖出不计盈亏）'
                      : '有成本基准'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {realizedByToken.length > 0 && (
          <div>
            <div className="section-label mb-1">各代币已实现盈亏</div>
            <ul className="space-y-0.5">
              {realizedByToken.map((row) => (
                <li key={row.token} className="flex flex-wrap items-baseline gap-x-1.5">
                  <span className="text-txt" title={row.token}>
                    {row.tokenSymbol ?? shortAddress(row.token)}
                  </span>
                  <QuoteText value={row} tone={pnlTone(row)} />
                </li>
              ))}
            </ul>
          </div>
        )}

        {excludedExternalProceeds.length > 0 && (
          <div>
            <div className="section-label mb-1">被排除的外部所得</div>
            <ul className="space-y-0.5">
              {excludedExternalProceeds.map((value) => (
                <li key={value.quote}>
                  <QuoteText value={value} />
                </li>
              ))}
            </ul>
            <p className="mt-1 text-[10.5px] text-warn">
              这部分卖出所得来自无成本批次，不计入盈亏
            </p>
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  )
}

function LeaderboardSection({ id }: { id: string }) {
  const [board, setBoard] = useState<Board>('mcap')
  const [page, setPage] = useState(1)

  const query = useQuery({
    queryKey: ['supernova-leaderboard', id, { board, page }],
    queryFn: () =>
      api.get<SupernovaLeaderboardPage>(`/supernova/campaigns/${id}/leaderboard`, {
        board,
        page,
        limit: PER_PAGE,
      }),
    retry: false,
  })

  const rows = query.data?.rows ?? []
  const total = query.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PER_PAGE))
  const snapshot = query.data?.snapshot ?? null
  // 导出必须钉在预览读到的那一份快照上。省掉 snapshotId 让服务端再挑一次，
  // 冻结那一刻的预览与导出会落在两份不同的快照上，而两边都说自己是终榜。
  const exportHref =
    `${API_BASE}/api/supernova/campaigns/${id}/export?board=${board}` +
    (snapshot === null ? '' : `&snapshotId=${snapshot.id}`)

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
            <Trophy className="size-4 text-cyan" />
          </div>
          <div>
            <h2 className="text-[15px] font-medium tracking-tight text-txt">榜单预览</h2>
            <p className="mt-0.5 text-[12px] text-mut2">
              与前台公布的是同一份数据（含递补名次与预计奖励），不是实时值。导出的 CSV 与这里同源。
            </p>
          </div>
        </div>

        {/*
          导出走浏览器直接打开而不是 fetch：服务端回的是 text/csv 附件，
          由浏览器自己落盘。用 fetch 读回来再造 Blob 只是把同一份数据在内存里过一遍。
        */}
        <Button asChild variant="outline" size="sm" className="pressable rounded-apple-sm">
          <a href={exportHref} target="_blank" rel="noreferrer">
            <Download className="size-3.5" />
            导出 CSV
          </a>
        </Button>
      </div>

      {snapshot && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-apple border border-line/60 px-3.5 py-2.5 text-[11.5px] text-mut">
          <span>
            快照 <span className="tnum font-mono text-txt">#{snapshot.id}</span> ·{' '}
            {snapshot.kind === 'final'
              ? '终榜'
              : snapshot.kind === 'hourly'
                ? '每小时'
                : snapshot.kind}
          </span>
          <span>
            cutoff{' '}
            <span className="tnum font-mono text-txt">{formatBlock(snapshot.cutoffBlock)}</span> ·{' '}
            {formatDateTime(snapshot.cutoffTime)}
          </span>
          {snapshot.stale && (
            <Badge className="h-5 bg-warn/15 text-[10.5px] text-warn hover:bg-warn/15">
              已陈旧
            </Badge>
          )}
          {query.data?.resultStatus && (
            <Badge
              className={cn(
                'h-5 text-[10.5px]',
                RESULT_STATUS_META[query.data.resultStatus]?.className ??
                  'bg-edge/10 text-mut hover:bg-edge/10',
              )}
            >
              {RESULT_STATUS_META[query.data.resultStatus]?.label ?? query.data.resultStatus}
            </Badge>
          )}
        </div>
      )}

      <Tabs
        value={board}
        onValueChange={(value) => {
          setBoard(value as Board)
          setPage(1)
        }}
        className="gap-4"
      >
        <TabsList>
          {BOARDS.map((item) => (
            <TabsTrigger key={item} value={item} className="h-9 text-[13px]">
              {BOARD_LABELS[item]}
            </TabsTrigger>
          ))}
        </TabsList>

        {BOARDS.map((item) => (
          <TabsContent key={item} value={item} className="space-y-4">
            <TableCard>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-line/50 hover:bg-transparent">
                      <TableHead className="section-label w-20 pl-6">名次</TableHead>
                      <TableHead className="section-label w-20">获奖名次</TableHead>
                      <TableHead className="section-label">
                        {item === 'mcap' ? '代币' : '钱包'}
                      </TableHead>
                      <TableHead className="section-label text-right">
                        {BOARD_METRIC_LABELS[item]}
                      </TableHead>
                      {/*
                        规则 v1.2 §2：交易量榜的计分额是内盘打过折的，原始成交额摆在
                        它旁边才看得出折扣在起作用 —— 只有一列时「刷了多少」和「算多少分」
                        是同一个数，内盘对刷就查不出来。
                      */}
                      {item === 'volume' && (
                        <>
                          <TableHead className="section-label text-right">原始成交额</TableHead>
                          {/*
                            交易量为 0 的第一解释。没有这一列时，「没交易」与「每一笔都太小」
                            在页面上是同一个 0，运营只能去翻快照明细的 Json。
                          */}
                          <TableHead
                            className="section-label text-right"
                            title="单笔低于最小有效交易额被跳过的笔数"
                          >
                            低于门槛
                          </TableHead>
                        </>
                      )}
                      {item === 'mcap' && (
                        <>
                          <TableHead className="section-label text-right">实时市值</TableHead>
                          {/* 规则 v1.2 §1 的获奖门槛输入值。不合格原因是「流动性不足」时，这一列就是证据。 */}
                          <TableHead className="section-label text-right">流动性</TableHead>
                        </>
                      )}
                      {item === 'pnl' && <TableHead className="section-label">明细</TableHead>}
                      <TableHead className="section-label">获奖资格</TableHead>
                      <TableHead className="section-label pr-6 text-right">预计奖励</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {query.isPending &&
                      Array.from({ length: 6 }, (_, index) => (
                        <TableRow key={index} className="border-line/40">
                          <TableCell colSpan={columnCount(item)} className="px-6 py-2.5">
                            <Skeleton className="h-7 w-full rounded-apple-sm" />
                          </TableCell>
                        </TableRow>
                      ))}

                    {query.isError && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={columnCount(item)}>
                          <EmptyState
                            icon={TriangleAlert}
                            title="读不到榜单"
                            description={errorMessage(query.error)}
                          />
                        </TableCell>
                      </TableRow>
                    )}

                    {query.isSuccess && rows.length === 0 && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={columnCount(item)}>
                          <EmptyState
                            icon={Trophy}
                            title="这张榜还没有数据"
                            description="活动尚未开始、还没跑过快照，或者没有符合上榜条件的对象。"
                          />
                        </TableCell>
                      </TableRow>
                    )}

                    {rows.map((row) => (
                      <TableRow
                        key={`${row.rank}-${row.subject}`}
                        className="row-hover border-line/40"
                      >
                        <TableCell className="tnum pl-6 font-mono text-[12px] text-txt">
                          {row.rank}
                        </TableCell>
                        <TableCell>
                          {row.awardRank ? (
                            <Badge
                              className={cn(
                                'h-5 tnum font-mono text-[10.5px]',
                                /*
                                  高亮的判据是**这个名次有没有奖**，不再是"前三"。
                                  奖励按名次区间配之后，第 4–10 名完全可能有奖、
                                  而第 2 名可能没有 —— 照前三高亮会标错人。
                                */
                                row.estRewardUsd !== null && row.estRewardUsd !== undefined
                                  ? 'bg-cyan/15 text-cyan hover:bg-cyan/15'
                                  : 'bg-edge/12 text-txt hover:bg-edge/12',
                              )}
                            >
                              {row.awardRank}
                            </Badge>
                          ) : (
                            <span className="text-dim">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {row.token?.symbol && (
                            <div className="text-[12.5px] text-txt">
                              {row.token.symbol}
                              {row.token.agentName && (
                                <span className="ml-1.5 text-[10.5px] text-dim">
                                  {row.token.agentName}
                                </span>
                              )}
                            </div>
                          )}
                          <RawId
                            value={row.subject}
                            href={
                              item === 'mcap'
                                ? explorerTokenUrl(row.subject)
                                : explorerAddressUrl(row.subject)
                            }
                          />
                        </TableCell>
                        <TableCell className="tnum text-right font-mono text-[12px] text-txt">
                          {formatUsd(row.metricUsd)}
                        </TableCell>
                        {item === 'volume' && (
                          <>
                            <TableCell className="tnum text-right font-mono text-[11.5px] text-mut">
                              {formatUsd(row.rawVolumeUsd)}
                            </TableCell>
                            <TableCell className="text-right">
                              <SkippedCell diagnostics={row.diagnostics} />
                            </TableCell>
                          </>
                        )}
                        {item === 'mcap' && (
                          <>
                            <TableCell className="tnum text-right font-mono text-[11.5px] text-mut">
                              {formatUsd(row.liveMcapUsd)}
                            </TableCell>
                            <TableCell className="tnum text-right font-mono text-[11.5px] text-mut">
                              {formatUsd(row.liquidityUsd)}
                            </TableCell>
                          </>
                        )}
                        {item === 'pnl' && (
                          <TableCell className="max-w-[28rem] align-top">
                            <PnlDetailCell diagnostics={row.diagnostics} />
                          </TableCell>
                        )}
                        <TableCell>
                          {row.eligible ? (
                            <Badge className="h-5 bg-green/15 text-[10.5px] text-green hover:bg-green/15">
                              合格
                            </Badge>
                          ) : (
                            <Badge
                              className="h-5 bg-edge/10 text-[10.5px] text-mut hover:bg-edge/10"
                              title={row.reason ?? ''}
                            >
                              {rowReasonLabel(row.reason)}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="tnum pr-6 text-right font-mono text-[12px] text-txt">
                          {formatUsd(row.estRewardUsd)}
                          {/*
                            规则 v1.6：参与奖（rest 区间）拿的不是名次奖。少了这一行，
                            一个第 300 名却有奖励的行看起来像名次区间配错了。
                          */}
                          {row.rewardKind === 'rest' && (
                            <div className="text-[10.5px] font-normal text-mut2">参与奖</div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TableCard>

            <div className="flex items-center justify-between gap-3">
              <Count value={total} unit="行" />
              <Pager page={page} pageCount={pageCount} onChange={setPage} />
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </section>
  )
}
